"use client";

import {
  useRef,
  useMemo,
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
  type ReactNode,
  type ClipboardEvent,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  useThreadRuntime,
  useComposerRuntime,
  useComposer,
  ThreadPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  useMessage,
  useThread,
} from "@assistant-ui/react";
import type {
  ChatModelAdapter,
  ThreadMessage,
  ThreadMessageLike,
} from "@assistant-ui/react";
import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
} from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import type {
  ChatAttachment,
  ChatSenderType,
  ChatUser,
  ClientContext,
  ToolCall,
} from "@maintainer-pro/ai-cli";
import { buildBrowserClientContext } from "./context";
import {
  ATTACH_ACCEPT,
  fileKindLabel,
  isAllowedBrowserFile,
  isImageMime,
  isImageView,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS,
  resolveBrowserMime,
  toAttachmentView,
  type AttachmentView,
  type PendingAttachment,
} from "./attachments";
import {
  WORKING_STATUS_INTERVAL_MS,
  WORKING_STATUS_MESSAGES,
  nextWorkingStatusIndex,
} from "./working-status";
import {
  fetchChatMessages,
  rememberConversationId,
  type StoredChatMessage,
} from "./chat-unread";
import {
  connectAiServerSocket,
  connectChatSocket,
  startChatRealtime,
  toAiServerWsUrl,
  type ChatSocket,
} from "./realtime";
import {
  createMaintainerProClient,
  waitForChatGoogleMessage,
} from "./maintainer-pro";
import { createAiUiLogger, previewText } from "./log";

export type { ChatUser, ChatSenderType };

const log = createAiUiLogger("chat");
const sendLog = createAiUiLogger("send");

function userBadge(user: ChatUser): string {
  if (user.role?.trim()) return user.role.trim();
  if (user.type === "ai") return "ai";
  if (user.type === "developer") return "dev";
  return user.type;
}

function chatRoleLabel(type?: string | null): "dev" | "client" | null {
  if (type === "developer") return "dev";
  if (type === "client") return "client";
  return null;
}

function SenderRoleBadge({ type }: { type?: string | null }) {
  const label = chatRoleLabel(type);
  if (!label) return null;
  return (
    <span
      className={`rounded-full px-1.5 text-[10px] font-semibold uppercase tracking-wide ${
        label === "dev"
          ? "bg-emerald-50 text-emerald-700"
          : "bg-stone-100 text-stone-600"
      }`}
    >
      {label}
    </span>
  );
}

export interface AiChatProps {
  apiUrl?: string;
  /**
   * Live client context for each turn. Defaults to the open page URL + route
   * via `buildBrowserClientContext()`.
   */
  buildContext?: () => ClientContext;
  onToolCalls?: (toolCalls: ToolCall[]) => void | Promise<void>;
  /**
   * Snapshot app state at the start of a turn so an interrupted turn can be
   * reverted when the user sends another message mid-run.
   */
  captureState?: () => unknown;
  /** Restore a snapshot previously returned by captureState. */
  restoreState?: (snapshot: unknown) => void;
  conversationId?: string;
  /**
   * @deprecated Ignored — sessions are shared via the server store.
   * Kept so existing call sites keep type-checking.
   */
  conversationStorageKey?: string;
  title?: string;
  emptyState?: ReactNode;
  className?: string;
  /**
   * People in this chat. Prefer omitting this when Maintainer Pro is configured
   * so accounts load from `/api/v1/client/chat-users` (roles included).
   * Client messages go to the AI; developer messages are human replies only.
   * Include `{ type: "ai", name }` to label AI replies for @mentions.
   */
  users?: ChatUser[];
  /**
   * When set, the widget sends chat over Maintainer Pro WebSocket first
   * (HTTP fallback). MP / ai-server coordinate the AI turn; `apiUrl` is
   * only used when the bridge is offline or the run fails.
   */
  maintainerProUrl?: string;
  maintainerProApiKey?: string;
  /** ai-server WebSocket URL. Defaults to `apiUrl` with path `/api/ws`. */
  aiServerWsUrl?: string;
}

type TurnControl = {
  snapshot: unknown;
  toolsApplied: boolean;
};

type PendingImage = PendingAttachment;

type MessageImagesApi = {
  imagesByMessageId: Record<string, AttachmentView[]>;
  registerImages: (
    messageId: string,
    items: Array<string | AttachmentView>
  ) => void;
};

type MessageSenderInfo = {
  name: string;
  type: ChatSenderType;
};

type MessageSendersApi = {
  senderByMessageId: Record<string, MessageSenderInfo>;
  registerSender: (messageId: string, sender: MessageSenderInfo) => void;
};

const MessageImagesContext = createContext<MessageImagesApi | null>(null);
const MessageSendersContext = createContext<MessageSendersApi | null>(null);
const AiUserContext = createContext<MessageSenderInfo | null>(null);
/** Logged-in chat user — used to align “self” bubbles on the right. */
const SelectedUserContext = createContext<ChatUser | null>(null);
/** All chat personas (client / developer / ai) — used for @mentions. */
const ChatUsersContext = createContext<ChatUser[]>([]);

type ReplyTarget = {
  id: string;
  name: string;
  snippet: string;
};

const ReplyTargetContext = createContext<{
  target: ReplyTarget | null;
  setTarget: (target: ReplyTarget | null) => void;
} | null>(null);

function resolveMessageSender(
  messageId: string,
  custom: MessageCustom | undefined,
  sendersApi: MessageSendersApi | null,
  fallback?: MessageSenderInfo | null
): MessageSenderInfo | null {
  return (
    sendersApi?.senderByMessageId[messageId] ??
    (custom?.senderName
      ? {
          name: custom.senderName,
          type: (custom.senderType as ChatSenderType) || "client",
        }
      : null) ??
    fallback ??
    null
  );
}

function persistSenderType(
  sender: { type?: string } | null | undefined
): "client" | undefined {
  return sender ? "client" : undefined;
}

/**
 * Chat UI alignment is by role, not who is signed in:
 * client → right, partner/developer + AI → left.
 */
type BubbleSide = "self" | "other" | "ai";

function resolveBubbleSide(
  _selectedUser: ChatUser | null,
  sender: MessageSenderInfo | null,
  role: string,
  isDeveloper: boolean
): BubbleSide {
  if (isDeveloper || sender?.type === "developer") return "other";
  if (sender?.type === "ai" || role === "assistant") return "ai";
  if (sender?.type === "client" || role === "user") return "self";
  return "other";
}

function isRightAligned(side: BubbleSide): boolean {
  return side === "self";
}

type BubbleColor = "indigo" | "white" | "emerald";

function bubbleColor(side: BubbleSide, isDeveloper: boolean): BubbleColor {
  if (side === "self") return "indigo";
  if (isDeveloper && side === "other") return "emerald";
  return "white";
}

/** Light quote chip — same surface on every bubble. Amber only when colors match. */
function replyQuoteClass(current: BubbleColor, parent: BubbleColor): string {
  return current === parent ? "aui-reply-quote-same" : "";
}

function isEmptyAssistantShell(m: ThreadMessage): boolean {
  if (m.role !== "assistant") return false;
  if (getMessageText(m).trim()) return false;
  const meta = m.metadata?.custom as MessageCustom | undefined;
  if (meta?.working) return true;
  const status = m.status?.type;
  return status === "running" || status === "incomplete";
}

function threadMessageMatchesId(m: ThreadMessage, id: string): boolean {
  if (m.id === id) return true;
  const custom = m.metadata?.custom as { dbId?: string } | undefined;
  return custom?.dbId === id;
}

/** Show a reply quote when the parent is not the immediate previous visible turn.
 * Matches Maintainer Pro admin `shouldShowAsReply`. */
function shouldShowAsReply(
  messages: readonly ThreadMessage[],
  messageId: string,
  parentId: string | undefined
): boolean {
  if (!parentId) return false;
  const parentIdx = messages.findIndex((m) => threadMessageMatchesId(m, parentId));
  const myIdx = messages.findIndex((m) => threadMessageMatchesId(m, messageId));
  if (parentIdx < 0 || myIdx < 0 || myIdx <= parentIdx) return false;
  for (let i = parentIdx + 1; i < myIdx; i++) {
    const row = messages[i]!;
    const meta = row.metadata?.custom as MessageCustom | undefined;
    // Skip hidden working placeholders (admin messageKind !== "working").
    if (row.role === "assistant" && meta?.working && !getMessageText(row).trim()) {
      continue;
    }
    return true;
  }
  return false;
}

function queryInScope(
  scope: ParentNode,
  messageId: string
): HTMLElement | null {
  const escaped =
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(messageId)
      : messageId.replace(/"/g, "");
  const byId =
    "getElementById" in scope
      ? (scope as Document | ShadowRoot).getElementById(`aui-msg-${messageId}`)
      : null;
  return (
    byId ??
    scope.querySelector<HTMLElement>(`[data-message-id="${escaped}"]`) ??
    scope.querySelector<HTMLElement>(`[data-db-id="${escaped}"]`)
  );
}

function scrollOverflowAncestor(el: HTMLElement) {
  let parent: HTMLElement | null = el.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      const parentBox = parent.getBoundingClientRect();
      const elBox = el.getBoundingClientRect();
      const offset =
        elBox.top - parentBox.top - parent.clientHeight / 2 + elBox.height / 2;
      parent.scrollTo({ top: parent.scrollTop + offset, behavior: "smooth" });
      return;
    }
    parent = parent.parentElement;
  }
  el.scrollIntoView({ behavior: "smooth", block: "center" });
}

function scrollToThreadMessage(from: EventTarget | null, messageId: string) {
  const start = from instanceof Element ? from : null;
  const root = start?.getRootNode();
  const scope: ParentNode =
    root instanceof ShadowRoot || root instanceof Document ? root : document;
  const el =
    queryInScope(scope, messageId) ??
    (scope !== document ? queryInScope(document, messageId) : null);
  if (!el) return;
  scrollOverflowAncestor(el);
  el.classList.add("aui-msg-flash");
  window.setTimeout(() => el.classList.remove("aui-msg-flash"), 1600);
}

function inferParentUserId(
  messages: readonly ThreadMessage[],
  messageId: string
): string | undefined {
  const myIdx = messages.findIndex((m) => m.id === messageId);
  if (myIdx <= 0) return undefined;
  for (let i = myIdx - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.role === "user") return m.id;
  }
  return undefined;
}

function truncateSnippet(text: string, max = 100): string {
  const t = text.trim().replace(/\s+/g, " ");
  if (!t) return "…";
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

function isComposableUser(user: ChatUser): boolean {
  return user.type === "client" || user.type === "developer";
}

function isAuthedSessionUser(
  user: ChatUser | null | undefined
): user is ChatUser {
  return Boolean(user?.email && isComposableUser(user));
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getActiveMention(
  text: string,
  cursor: number
): { start: number; query: string } | null {
  const before = text.slice(0, cursor);
  const match = before.match(/@([\w.-]*)$/);
  if (!match) return null;
  return {
    start: cursor - match[0].length,
    query: match[1] ?? "",
  };
}

const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

function splitUrl(raw: string): { href: string; trailing: string } {
  let href = raw;
  let trailing = "";
  const punctuation = href.match(/[.,!?;:'"]+$/);
  if (punctuation) {
    trailing = punctuation[0];
    href = href.slice(0, -trailing.length);
  }
  while (
    href.endsWith(")") &&
    (href.match(/\(/g)?.length ?? 0) < (href.match(/\)/g)?.length ?? 0)
  ) {
    trailing = `)${trailing}`;
    href = href.slice(0, -1);
  }
  while (
    href.endsWith("]") &&
    (href.match(/\[/g)?.length ?? 0) < (href.match(/\]/g)?.length ?? 0)
  ) {
    trailing = `]${trailing}`;
    href = href.slice(0, -1);
  }
  return { href, trailing };
}

function linkNodes(
  text: string,
  keyPrefix: string,
  linkClassName: string
): ReactNode[] {
  if (!text) return [];
  const nodes: ReactNode[] = [];
  const re = new RegExp(URL_PATTERN.source, "gi");
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const { href, trailing } = splitUrl(match[0]);
    const url = /^www\./i.test(href) ? `https://${href}` : href;
    if (/^https?:\/\//i.test(url)) {
      nodes.push(
        <a
          key={`${keyPrefix}-${match.index}`}
          href={url}
          target="_blank"
          rel="noreferrer"
          className={linkClassName}
        >
          {href}
        </a>
      );
    } else if (href) {
      nodes.push(href);
    }
    if (trailing) nodes.push(trailing);
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length > 0 ? nodes : [text];
}

function MentionText({
  text,
  tone,
}: {
  text: string;
  tone: "user" | "assistant";
}) {
  const users = useContext(ChatUsersContext);
  const names = [
    ...new Set(users.map((u) => u.name.trim()).filter(Boolean)),
  ].sort((a, b) => b.length - a.length);
  const linkClassName = tone === "user" ? "aui-link aui-link-self" : "aui-link";

  if (names.length === 0 || !text) {
    return <>{linkNodes(text, "url", linkClassName)}</>;
  }

  const pattern = new RegExp(
    `(@(?:${names.map(escapeRegExp).join("|")}))(?=$|\\s|[.,!?;:'")\\]])`,
    "g"
  );
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        ...linkNodes(
          text.slice(lastIndex, match.index),
          `url-${lastIndex}`,
          linkClassName
        )
      );
    }
    nodes.push(
      <span
        key={`${match.index}-${match[1]}`}
        className={
          tone === "user"
            ? "font-semibold text-white underline decoration-white/50 underline-offset-2"
            : "font-semibold text-indigo-600"
        }
      >
        {match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(
      ...linkNodes(text.slice(lastIndex), `url-${lastIndex}`, linkClassName)
    );
  }
  return <>{nodes}</>;
}

function userKey(user: ChatUser, index: number): string {
  return `${user.type}:${user.name}:${index}`;
}

function keyForClientEmail(
  composableUsers: ChatUser[],
  email: string
): string | null {
  const needle = email.toLowerCase();
  const index = composableUsers.findIndex(
    (u) => u.email && u.email.toLowerCase() === needle
  );
  if (index < 0) return null;
  return userKey(composableUsers[index]!, index);
}

/**
 * Admin session cookie on this origin. Client users only — partners are
 * resolved via `/api/v1/client/chat-session` so access is scoped to this app.
 */
async function readSameOriginClientUser(): Promise<ChatUser | null> {
  if (typeof window === "undefined") return null;
  try {
    const res = await fetch("/api/v1/auth/me", {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      actor?: {
        kind?: string;
        email?: string;
        name?: string;
        role?: string;
      };
    };
    const actor = data.actor;
    if (actor?.kind !== "client_user" || !actor.email) return null;
    return {
      name: actor.name || actor.email,
      email: actor.email,
      type: "client",
      role: actor.role,
    };
  } catch {
    return null;
  }
}

/** Normalize provider ids used in storage / detect to a display fallback name. */
function providerDisplayName(providerId: string | null | undefined): string | null {
  const id = (providerId ?? "").toLowerCase().trim();
  if (!id || id === "developer") return null;
  if (id === "claude") return "claude";
  if (id === "cursor") return "cursor";
  if (id === "antigravity" || id === "agy") return "antigravity";
  return providerId!.trim();
}

/**
 * Default AI identity: name from `users` when type "ai" is listed,
 * otherwise the active provider id (claude | cursor | antigravity).
 */
function resolveAiUser(
  users: ChatUser[] | undefined,
  providerId: string | null | undefined
): MessageSenderInfo {
  const fromList = users?.find((u) => u.type === "ai");
  const fromListName = fromList?.name.trim();
  return {
    type: "ai",
    name: fromListName || providerDisplayName(providerId) || "ai",
  };
}

function senderFromStoredMessage(
  m: {
    role: string;
    provider?: string | null;
    senderType?: string | null;
    senderName?: string | null;
  },
  aiUser: MessageSenderInfo
): MessageSenderInfo | null {
  if (m.senderType === "developer" || m.provider === "developer") {
    return {
      name: m.senderName?.trim() || "Developer",
      type: "developer",
    };
  }
  if (m.senderType === "client" || (m.role === "user" && m.senderName)) {
    return {
      name: m.senderName?.trim() || "Client",
      type: "client",
    };
  }
  if (
    m.role === "assistant" &&
    (m.senderType === "ai" ||
      (m.provider && m.provider !== "developer") ||
      m.senderType == null)
  ) {
    return {
      type: "ai",
      name:
        m.senderName?.trim() ||
        (m.provider === "working"
          ? aiUser.name
          : providerDisplayName(m.provider)) ||
        aiUser.name,
    };
  }
  return null;
}

function newMessageId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function persistDeveloperReply(
  apiUrl: string,
  conversationId: string | undefined,
  content: string,
  senderName: string,
  maintainerProUrl?: string,
  maintainerProApiKey?: string,
  id?: string,
  parentMessageId?: string
): void {
  if (!conversationId || !content.trim()) return;
  if (maintainerProUrl && maintainerProApiKey) {
    const mp = createMaintainerProClient({
      baseUrl: maintainerProUrl,
      apiKey: maintainerProApiKey,
    });
    void mp
      .sendDeveloperMessage({
        conversationId,
        id,
        content,
        senderName,
        parentMessageId,
      })
      .catch(() => undefined);
    return;
  }
  void fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "reply",
      conversationId,
      messageId: id,
      parentMessageId,
      content,
      senderName,
      senderType: "developer",
    }),
  }).catch(() => {
    // Reply persistence is best-effort; the live bubble still shows.
  });
}

function fileToPending(file: File): Promise<PendingImage> {
  return new Promise((resolve, reject) => {
    const mimeType = resolveBrowserMime(file);
    if (!isAllowedBrowserFile(file)) {
      reject(
        new Error(
          "Use an image (PNG/JPG/WebP/GIF), PDF, Word, or Excel file"
        )
      );
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      reject(
        new Error(
          `File too large (max ${MAX_ATTACHMENT_BYTES / (1024 * 1024)}MB)`
        )
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result ?? "");
      const base64 = dataUrl.includes(",")
        ? dataUrl.split(",")[1] ?? ""
        : dataUrl;
      resolve({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: file.name || (isImageMime(mimeType) ? "screenshot.png" : "attachment"),
        mimeType: mimeType || file.type || "application/octet-stream",
        dataUrl,
        base64,
      });
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function getMessageText(message: {
  content: readonly { type: string; text?: string }[];
}): string {
  return message.content
    .filter((p) => p.type === "text")
    .map((p) => p.text ?? "")
    .join("");
}

function takeSnapshot(captureState?: () => unknown): unknown {
  if (!captureState) return undefined;
  try {
    return structuredClone(captureState());
  } catch {
    return captureState();
  }
}

function isLikelyStatusAck(content: string): boolean {
  const text = content.trim();
  if (!text || text.length > 96) return false;
  return /^(on it|sure|yep|got it|okay|ok|alright|one sec|hang on)/i.test(
    text
  );
}

/** `/p/:token/:app` share URL used by admin “Open app”, when this page is proxied. */
function resolveProxyAppUrl(): string | null {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname;
  if (/^\/p\/[^/]+\/[^/]+/.test(path)) {
    return window.location.href;
  }
  const apiUrl = (
    window as Window & { __MAINTAINER_PRO__?: { apiUrl?: string } }
  ).__MAINTAINER_PRO__?.apiUrl;
  if (!apiUrl) return null;
  try {
    const u = new URL(apiUrl, window.location.origin);
    const match = u.pathname.match(/^(\/p\/[^/]+)\/ai(?:\/|$)/);
    if (!match) return null;
    return `${u.origin}${match[1]}/ui`;
  } catch {
    return null;
  }
}

function refreshHostApp() {
  const proxyUrl = resolveProxyAppUrl();
  if (!proxyUrl) {
    window.location.reload();
    return;
  }
  try {
    const next = new URL(proxyUrl, window.location.href);
    const here = new URL(window.location.href);
    if (
      next.origin === here.origin &&
      next.pathname === here.pathname &&
      next.search === here.search
    ) {
      window.location.reload();
      return;
    }
    window.location.assign(next.toString());
  } catch {
    window.location.reload();
  }
}

/** Drop incomplete/working assistants; keep each user turn as its own prompt. */
function prepareApiMessages(
  messages: readonly ThreadMessage[]
): Array<{ role: "user" | "assistant"; content: string }> {
  return messages
    .filter((m) => {
      if (m.role !== "assistant") return true;
      const status = m.status?.type;
      if (status === "incomplete" || status === "running") return false;
      const custom = m.metadata?.custom as { working?: boolean } | undefined;
      if (custom?.working) return false;
      return Boolean(getMessageText(m).trim());
    })
    .map((m) => ({
      role: m.role as "user" | "assistant",
      content: getMessageText(m),
    }));
}

type MessageCustom = {
  senderName?: string;
  senderType?: string;
  working?: boolean;
  parentMessageId?: string;
  createdAt?: string;
  queueStatus?: string | null;
  dbId?: string;
};

function formatMessageTime(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (sameDay) return time;
  const sameYear = d.getFullYear() === now.getFullYear();
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  return `${date} · ${time}`;
}

function MessageMeta({
  name,
  createdAt,
  align = "left",
  nameClassName = "text-gray-400",
  badge,
}: {
  name?: string | null;
  createdAt?: string | null;
  align?: "left" | "right";
  nameClassName?: string;
  badge?: ReactNode;
}) {
  const time = formatMessageTime(createdAt);
  if (!name && !time) return null;
  return (
    <p
      className={`mb-1 flex items-baseline gap-2 text-[11px] font-medium ${
        align === "right" ? "justify-end text-right" : "justify-start text-left"
      } ${nameClassName}`}
    >
      {name ? <span>{name}</span> : null}
      {badge}
      {time ? (
        <time
          dateTime={createdAt ?? undefined}
          className="font-normal text-gray-400"
        >
          {time}
        </time>
      ) : null}
    </p>
  );
}

type QueuedSend = {
  id: string;
  dbId?: string;
  text: string;
  images: PendingImage[];
  sender: ChatUser | null;
  status?: "queued" | "working";
  createdAt: string;
  parentMessageId?: string;
};

/** Fingerprints of queued user texts — RemoteMessageSync must not re-append these. */
const queuedUserFingerprints = new Set<string>();

function syncQueuedFingerprints(queue: QueuedSend[]) {
  queuedUserFingerprints.clear();
  for (const item of queue) {
    const text = item.text.trim();
    if (text) queuedUserFingerprints.add(contentFingerprint("user", text));
  }
}

type SendBatch = {
  images: PendingImage[];
  sender: ChatUser | null;
  /** User row already saved when the bubble was queued. */
  skipPersistUser?: boolean;
  /** Persisted user row id (admin / store). */
  userMessageId?: string;
};

async function sendAiChatRun(
  socket: ChatSocket | null | undefined,
  payload: {
    conversationId?: string;
    userMessage: string;
    userMessageId?: string;
    sender?: ChatUser | null;
    messages?: { role: string; content: string }[];
    context?: ClientContext;
  }
): Promise<boolean> {
  if (!socket || !payload.conversationId || !payload.userMessage.trim()) {
    sendLog.warn(
      {
        hasSocket: Boolean(socket),
        socketOpen: Boolean(socket?.isOpen()),
        conversationId: payload.conversationId ?? null,
        userMessageId: payload.userMessageId ?? null,
        emptyText: !payload.userMessage.trim(),
        preview: previewText(payload.userMessage),
      },
      "chat.run skipped (missing socket/conversation/text)"
    );
    return false;
  }
  sendLog.info(
    {
      conversationId: payload.conversationId,
      userMessageId: payload.userMessageId ?? null,
      socketOpen: socket.isOpen(),
      history: payload.messages?.length ?? 0,
      preview: previewText(payload.userMessage),
    },
    "chat.run waiting"
  );
  try {
    if (!socket.isOpen()) await socket.waitUntilOpen(4_000);
  } catch (err) {
    sendLog.error(
      { err, conversationId: payload.conversationId },
      "chat.run waitUntilOpen failed"
    );
    return false;
  }
  const sent = socket.send({
    type: "chat.run",
    conversationId: payload.conversationId,
    userMessage: payload.userMessage,
    content: payload.userMessage,
    userMessageId: payload.userMessageId,
    skipPersistUser: true,
    senderType: persistSenderType(payload.sender),
    senderName: payload.sender?.name,
    messages: payload.messages,
    context: payload.context,
  });
  sendLog.info(
    {
      sent,
      conversationId: payload.conversationId,
      userMessageId: payload.userMessageId ?? null,
      socketOpen: socket.isOpen(),
    },
    sent ? "chat.run sent" : "chat.run send returned false"
  );
  return sent;
}

async function persistUserMessage(
  apiUrl: string,
  conversationId: string | undefined,
  content: string,
  sender: ChatUser | null,
  images: PendingImage[],
  queueStatus?: "working" | "queued",
  id?: string,
  parentMessageId?: string
): Promise<{
  id?: string;
  queueStatus?: string | null;
} | null> {
  if (!conversationId || (!content.trim() && images.length === 0)) return null;
  const attachments: ChatAttachment[] = images.map((img) => ({
    name: img.name,
    mimeType: img.mimeType,
    data: img.base64,
  }));
  log.debug(
    {
      conversationId,
      queueStatus,
      attachments: attachments.length,
      preview: previewText(content),
    },
    "persistUserMessage http"
  );
  try {
    const res = await fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "user",
        conversationId,
        messageId: id,
        parentMessageId,
        content,
        senderName: sender?.name,
        senderType: persistSenderType(sender),
        attachments: attachments.length > 0 ? attachments : undefined,
        queueStatus,
      }),
    });
    const data = (await res.json().catch(() => null)) as {
      message?: { id?: string; queueStatus?: string | null };
      queueStatus?: string | null;
    } | null;
    if (!res.ok) {
      log.warn({ status: res.status, conversationId }, "persistUserMessage failed");
      return null;
    }
    return {
      id: data?.message?.id,
      queueStatus: data?.queueStatus ?? data?.message?.queueStatus ?? null,
    };
  } catch (err) {
    log.warn({ err, conversationId }, "persistUserMessage error");
    return null;
  }
}

function useStableAdapter(options: {
  apiUrl: string;
  buildContext: () => ClientContext;
  onToolCalls?: (toolCalls: ToolCall[]) => void | Promise<void>;
  captureState?: () => unknown;
  restoreState?: (snapshot: unknown) => void;
  conversationId?: string;
  takePending: () => PendingImage[];
  takeSendBatch: () => SendBatch | null;
  registerImages: (
    messageId: string,
    items: Array<string | AttachmentView>
  ) => void;
  registerSender: (messageId: string, sender: MessageSenderInfo) => void;
  getSelectedUser: () => ChatUser | null;
  onProvider?: (providerId: string) => void;
  turnRef: { current: TurnControl };
  onQueuedNextRef?: {
    current: ((next: { id: string; content: string }) => void) | null;
  };
  aiServerSocket?: ChatSocket | null;
}): ChatModelAdapter {
  const optsRef = useRef(options);
  optsRef.current = options;

  return useMemo<ChatModelAdapter>(
    () => ({
      async *run({ messages, abortSignal }) {
        const {
          apiUrl,
          buildContext,
          onToolCalls,
          captureState,
          restoreState,
          conversationId,
          takePending,
          takeSendBatch,
          registerImages,
          registerSender,
          getSelectedUser,
          onProvider,
          turnRef,
          onQueuedNextRef,
          aiServerSocket,
        } = optsRef.current;

        turnRef.current = {
          snapshot: takeSnapshot(captureState),
          toolsApplied: false,
        };

        const batch = takeSendBatch();
        const selectedUser = batch ? batch.sender : getSelectedUser();
        const pending = batch ? batch.images : takePending();
        const skipPersistUser = Boolean(batch?.skipPersistUser);
        const lastUser = [...messages]
          .reverse()
          .find((m) => m.role === "user") as { id?: string } | undefined;
        if (lastUser?.id && pending.length > 0) {
          registerImages(
            lastUser.id,
            pending.map((img) => ({
              url: img.dataUrl,
              name: img.name,
              mimeType: img.mimeType,
            }))
          );
        }
        if (lastUser?.id && selectedUser) {
          registerSender(lastUser.id, {
            name: selectedUser.name,
            type: persistSenderType(selectedUser) ?? "client",
          });
        }

        const apiMessages = prepareApiMessages(messages as ThreadMessage[]);
        // Persist only this bubble's text — not a combined multi-turn prompt.
        const rawUserMessage = lastUser
          ? getMessageText(lastUser as ThreadMessage)
          : "";
        sendLog.info(
          {
            conversationId,
            skipPersistUser,
            userMessageId: batch?.userMessageId ?? null,
            aiServerOpen: Boolean(aiServerSocket?.isOpen()),
            history: apiMessages.length,
            preview: previewText(rawUserMessage),
          },
          "adapter.run start"
        );

        const attachments: ChatAttachment[] = pending.map((img) => ({
          name: img.name,
          mimeType: img.mimeType,
          data: img.base64,
        }));

        const request = (async () => {
          const ranOnWs = await sendAiChatRun(aiServerSocket, {
            conversationId,
            userMessage: rawUserMessage,
            userMessageId: batch?.userMessageId,
            sender: selectedUser,
            messages: apiMessages,
            context: buildContext(),
          });
          if (ranOnWs) {
            sendLog.info({ conversationId }, "adapter.run dispatched on ai-server ws");
            return { text: "", viaAiServerWs: true };
          }
          sendLog.info({ conversationId, apiUrl }, "adapter.run falling back to http POST");
          const res = await fetch(apiUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              messages: apiMessages,
              userMessage: rawUserMessage || undefined,
              skipPersistUser: skipPersistUser || Boolean(batch?.userMessageId),
              userMessageId: batch?.userMessageId,
              context: buildContext(),
              conversationId,
              attachments:
                batch?.userMessageId || skipPersistUser
                  ? undefined
                  : attachments.length > 0
                    ? attachments
                    : undefined,
              senderType: persistSenderType(selectedUser),
              senderName: selectedUser?.name,
            }),
            signal: abortSignal,
          });

          const data = (await res.json()) as {
            text?: string;
            toolCalls?: ToolCall[];
            error?: string;
            provider?: string;
            superseded?: boolean;
            queued?: boolean;
            nextQueued?: { id: string; content: string } | null;
          };

          if (data.superseded) {
            return data;
          }

          if (!res.ok) {
            throw new Error(
              data.error ?? `Failed to fetch from ${apiUrl} (${res.status})`
            );
          }

          return data;
        })();

        // Single running assistant bubble — progress UI until the real reply.
        yield {
          content: [{ type: "text", text: "" }],
        };

        let data: {
          text?: string;
          toolCalls?: ToolCall[];
          error?: string;
          provider?: string;
          superseded?: boolean;
          queued?: boolean;
          nextQueued?: { id: string; content: string } | null;
        };
        try {
          data = await request;
        } catch (err) {
          if (abortSignal.aborted) {
            if (turnRef.current.toolsApplied) {
              restoreState?.(turnRef.current.snapshot);
              turnRef.current.toolsApplied = false;
            }
            return;
          }
          const message =
            err instanceof Error && err.message.trim()
              ? err.message.trim()
              : "Sorry about that — I couldn't finish this right now. I can connect you with a developer if you'd like someone to take a look.";
          yield {
            content: [{ type: "text", text: message }],
          };
          return;
        }
        if (abortSignal.aborted) {
          if (turnRef.current.toolsApplied) {
            restoreState?.(turnRef.current.snapshot);
            turnRef.current.toolsApplied = false;
          }
          return;
        }

        // Another turn won the conversation race (rare with sequential queue).
        if (data.superseded) {
          return;
        }
        if (data.queued) {
          return;
        }
        if ((data as { viaAiServerWs?: boolean }).viaAiServerWs) {
          return;
        }

        if (data.provider) {
          onProvider?.(data.provider);
        }

        const toolCalls = data.toolCalls ?? [];
        let text = data.text ?? "";

        if (toolCalls.length > 0) {
          await onToolCalls?.(toolCalls);
          turnRef.current.toolsApplied = true;
        }

        if (abortSignal.aborted) {
          if (turnRef.current.toolsApplied) {
            restoreState?.(turnRef.current.snapshot);
            turnRef.current.toolsApplied = false;
          }
          return;
        }

        if (toolCalls.length > 0 && !text.trim()) {
          text =
            "Done — please check the updated UI. If you don't see the change, refresh this page.";
        }

        yield {
          content: [{ type: "text", text }],
        };
        if (data.nextQueued?.id) {
          queueMicrotask(() => {
            onQueuedNextRef?.current?.(data.nextQueued!);
          });
        }
      },
    }),
    []
  );
}

const markdownComponents = memoizeMarkdownComponents({
  h1: ({ children }) => (
    <h1 className="mb-2 mt-3 text-base font-semibold first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-3 text-sm font-semibold first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1.5 mt-2 text-sm font-semibold first:mt-0">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
      {children}
    </p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="aui-link break-all"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-gray-300 pl-3 text-gray-600 italic last:mb-0">
      {children}
    </blockquote>
  ),
  code: ({ className, children, ...props }) => {
    const isBlock = Boolean(className?.includes("language-"));
    if (isBlock) {
      return (
        <code className={`${className ?? ""} text-xs`} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded-lg bg-gray-900 p-3 text-xs text-gray-100 last:mb-0">
      {children}
    </pre>
  ),
  hr: () => <hr className="my-3 border-gray-200" />,
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="w-full border-collapse text-left text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-gray-200 px-2 py-1 font-semibold">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-b border-gray-100 px-2 py-1 align-top">{children}</td>
  ),
});

function MarkdownText() {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md"
      smooth={false}
      components={markdownComponents}
    />
  );
}

function ReplyQuote({
  parentMessageId,
  currentSide,
  isDeveloper = false,
}: {
  parentMessageId: string;
  currentSide: BubbleSide;
  isDeveloper?: boolean;
}) {
  const messages = useThread((t) => t.messages);
  const sendersApi = useContext(MessageSendersContext);
  const selectedUser = useContext(SelectedUserContext);
  const parent = useMemo(
    () =>
      messages.find((m) => threadMessageMatchesId(m, parentMessageId)) ?? null,
    [messages, parentMessageId]
  );
  const parentCustom = parent?.metadata?.custom as MessageCustom | undefined;
  const parentSender = parent
    ? resolveMessageSender(parent.id, parentCustom, sendersApi)
    : null;
  const snippet = parent
    ? truncateSnippet(getMessageText(parent))
    : "Original message";
  const parentSide = parent
    ? resolveBubbleSide(
        selectedUser,
        parentSender,
        parent.role,
        isDeveloper
      )
    : "ai";
  const tone = replyQuoteClass(
    bubbleColor(currentSide, isDeveloper),
    bubbleColor(parentSide, isDeveloper)
  );

  return (
    <button
      type="button"
      className={`aui-reply-quote ${tone}`}
      aria-label="Go to original message"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        scrollToThreadMessage(
          event.currentTarget,
          parent?.id ?? parentMessageId
        );
      }}
    >
      <p className="aui-reply-quote-name">
        {parentSender?.name ?? "Message"}
      </p>
      <p className="aui-reply-quote-body">{snippet}</p>
    </button>
  );
}

function ReplyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 17 4 12l5-5" />
      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </svg>
  );
}

function ReplyAction({
  messageId,
  dbId,
  name,
  snippet,
}: {
  messageId: string;
  dbId?: string;
  name: string;
  snippet: string;
}) {
  const reply = useContext(ReplyTargetContext);
  const selectedUser = useContext(SelectedUserContext);
  if (!reply || !selectedUser) return null;
  const text = snippet.trim();
  if (!text) return null;
  return (
    <button
      type="button"
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-[0_2px_8px_rgb(15_23_42/0.16)] hover:bg-gray-50 hover:text-gray-800"
      aria-label="Reply to this message"
      onClick={() =>
        reply.setTarget({
          id: dbId ?? messageId,
          name,
          snippet: truncateSnippet(text, 80),
        })
      }
    >
      <ReplyIcon />
    </button>
  );
}

function UserMessage() {
  const message = useMessage();
  const imagesApi = useContext(MessageImagesContext);
  const sendersApi = useContext(MessageSendersContext);
  const selectedUser = useContext(SelectedUserContext);
  const images = imagesApi?.imagesByMessageId[message.id] ?? [];
  const custom = message.metadata?.custom as MessageCustom | undefined;
  const sender = resolveMessageSender(message.id, custom, sendersApi);
  const side = resolveBubbleSide(selectedUser, sender, message.role, false);
  const onRight = isRightAligned(side);
  const liveCreatedAtRef = useRef<string | null>(null);
  if (liveCreatedAtRef.current == null) {
    liveCreatedAtRef.current = custom?.createdAt ?? new Date().toISOString();
  }
  const createdAt = custom?.createdAt ?? liveCreatedAtRef.current;

  const messages = useThread((t) => t.messages);
  const liveStatus = custom?.queueStatus ?? null;
  const showWorking = useMemo(() => {
    const idx = messages.findIndex((m) => m.id === message.id);
    let working = liveStatus === "working";

    for (const m of messages) {
      if (m.role !== "assistant") continue;
      const meta = m.metadata?.custom as MessageCustom | undefined;
      if (meta?.parentMessageId === message.id) {
        if (meta.working && !getMessageText(m).trim()) {
          working = true;
          break;
        }
      }
    }

    if (!working && idx >= 0) {
      for (let i = idx + 1; i < messages.length; i++) {
        const m = messages[i]!;
        if (m.role === "user") break;
        const meta = m.metadata?.custom as MessageCustom | undefined;
        if (meta?.parentMessageId && meta.parentMessageId !== message.id) {
          continue;
        }
        if (meta?.working && !getMessageText(m).trim()) {
          working = true;
          break;
        }
        if (m.role === "assistant" && getMessageText(m).trim()) break;
      }
    }

    // Real reply already present for this parent — drop working.
    if (
      messages.some((m) => {
        if (m.role !== "assistant") return false;
        const meta = m.metadata?.custom as MessageCustom | undefined;
        return (
          meta?.parentMessageId === message.id &&
          !meta.working &&
          Boolean(getMessageText(m).trim())
        );
      })
    ) {
      working = false;
    }

    return working;
  }, [messages, message.id, liveStatus]);

  const queue = useContext(MessageQueueContext)?.queue ?? [];
  const queuedItem = queue.find(
    (q) =>
      q.id === message.id ||
      (custom?.dbId && q.dbId === custom.dbId) ||
      (q.dbId && q.dbId === message.id)
  );
  const queuedStatus = liveStatus ?? queuedItem?.status ?? custom?.queueStatus;
  const queueNumber = queuedRankFromThread(
    messages,
    message.id,
    custom?.dbId
  );
  const showQueued =
    queuedStatus === "queued" && !showWorking;
  const parentId = custom?.parentMessageId;
  const showReply = Boolean(parentId);

  return (
    <MessagePrimitive.Root
      className={`flex px-4 py-1 ${onRight ? "justify-end" : "justify-start"}`}
    >
      <div
        id={`aui-msg-${message.id}`}
        data-message-id={message.id}
        data-db-id={custom?.dbId}
        className="min-w-0 max-w-[80%]"
      >
        <MessageMeta
          name={
            sender?.name ||
            (sender?.type === "developer"
              ? "Dev"
              : sender?.type === "client"
                ? "Client"
                : undefined)
          }
          createdAt={createdAt}
          align={onRight ? "right" : "left"}
          badge={<SenderRoleBadge type={sender?.type} />}
        />
        <div
          className={`flex min-w-0 items-center gap-1.5 ${
            onRight ? "flex-row-reverse" : ""
          }`}
        >
        <div
          className={`min-w-0 max-w-full rounded-2xl px-4 py-2.5 text-sm ${
            side === "self"
              ? "rounded-br-sm bg-indigo-600 text-white"
              : "rounded-bl-sm border border-gray-200 bg-white text-gray-800 shadow-sm"
          }`}
        >
          {showReply && parentId ? (
            <ReplyQuote parentMessageId={parentId} currentSide={side} />
          ) : null}
          {images.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {images.map((raw, index) => {
                const item = toAttachmentView(raw);
                const image = isImageView(item);
                return (
                  <a
                    key={`${message.id}-${index}`}
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className={`block overflow-hidden rounded-lg border ${
                      side === "self"
                        ? "border-white/30 bg-white/10"
                        : "border-gray-200 bg-gray-50"
                    } ${image ? "" : "max-w-[14rem] px-2.5 py-2 text-xs"}`}
                  >
                    {image ? (
                      <img
                        src={item.url}
                        alt={item.name || `Attached image ${index + 1}`}
                        className="max-h-40 max-w-full object-contain"
                      />
                    ) : (
                      <span className="flex items-center gap-2">
                        <span className="rounded bg-black/10 px-1.5 py-0.5 font-semibold uppercase tracking-wide">
                          {fileKindLabel(item.mimeType, item.name)}
                        </span>
                        <span className="truncate">
                          {item.name || `Attachment ${index + 1}`}
                        </span>
                      </span>
                    )}
                  </a>
                );
              })}
            </div>
          )}
          <p className="aui-plain whitespace-pre-wrap break-words">
            <MentionText
              text={getMessageText(message)}
              tone={side === "self" ? "user" : "assistant"}
            />
          </p>
        </div>
        <ReplyAction
          messageId={message.id}
          dbId={custom?.dbId}
          name={sender?.name ?? "Message"}
          snippet={getMessageText(message)}
        />
        </div>
        {showWorking && (
          <div
            className={`mt-1.5 flex ${onRight ? "justify-end" : "justify-start"}`}
          >
            <RunningProgress />
          </div>
        )}
        {showQueued && (
          <div
            className={`mt-1.5 flex ${onRight ? "justify-end" : "justify-start"}`}
          >
            <RunningProgress
              label={
                queueNumber != null ? `In queue · #${queueNumber}` : "In queue"
              }
            />
          </div>
        )}
      </div>
    </MessagePrimitive.Root>
  );
}

function useRotatingWorkingStatus(fixed?: string) {
  const [index, setIndex] = useState(() => nextWorkingStatusIndex());
  useEffect(() => {
    if (fixed) return;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;
    const id = window.setInterval(() => {
      setIndex((prev) => nextWorkingStatusIndex(prev));
    }, WORKING_STATUS_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [fixed]);
  return fixed ?? WORKING_STATUS_MESSAGES[index]!;
}

function RunningProgress({ label }: { label?: string }) {
  const text = useRotatingWorkingStatus(label);
  return (
    <div
      className="flex items-center gap-2.5 text-gray-500"
      role="status"
      aria-label="Working on your request"
      aria-live="off"
      aria-busy="true"
    >
      <span className="flex items-center gap-1" aria-hidden>
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-500 [animation-delay:-0.3s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-500 [animation-delay:-0.15s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-500" />
      </span>
      <span className="text-xs font-medium tracking-wide">{text}</span>
    </div>
  );
}

function DefaultEmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <p className="text-[15px] font-semibold text-gray-800">
        What should we change?
      </p>
      <p className="max-w-[16.5rem] text-sm leading-relaxed text-gray-500">
        A bug, a layout tweak, or a feature idea — describe it and I’ll work on
        this app with you.
      </p>
      <p className="max-w-[16.5rem] text-xs leading-relaxed text-gray-400">
        Try “fix the spacing on this page” or “add a search box at the top”.
      </p>
    </div>
  );
}

function MissingCliBanner({
  onRecheck,
  checking,
}: {
  onRecheck?: () => void;
  checking?: boolean;
}) {
  return (
    <div className="mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-950">
      <p className="font-semibold">Chat needs a coding agent CLI</p>
      <p className="mt-1 text-[13px] leading-relaxed text-amber-900/85">
        This project folder has no signed-in{" "}
        <code className="rounded bg-amber-100 px-1">agent</code>,{" "}
        <code className="rounded bg-amber-100 px-1">claude</code>, or{" "}
        <code className="rounded bg-amber-100 px-1">agy</code>. Install one,
        or set <code className="rounded bg-amber-100 px-1">AI_CLI_COMMAND</code>{" "}
        in the folder <code className="rounded bg-amber-100 px-1">.env</code>,
        then restart the Maintainer Pro command.
      </p>
      <ul className="mt-2 space-y-1 text-[13px]">
        <li>
          <a
            href="https://cursor.com"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 hover:text-amber-800"
          >
            Cursor Agent CLI
          </a>{" "}
          (<code>agent</code>)
        </li>
        <li>
          <a
            href="https://docs.anthropic.com/en/docs/claude-code"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 hover:text-amber-800"
          >
            Claude Code
          </a>{" "}
          (<code>claude</code>)
        </li>
        <li>
          <a
            href="https://antigravity.google/"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 hover:text-amber-800"
          >
            Antigravity
          </a>{" "}
          (<code>agy</code>)
        </li>
      </ul>
      {onRecheck ? (
        <button
          type="button"
          onClick={onRecheck}
          disabled={checking}
          className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-950 hover:bg-amber-100 disabled:opacity-50"
        >
          {checking ? "Checking…" : "Check again"}
        </button>
      ) : null}
    </div>
  );
}

function AssistantMessage() {
  const message = useMessage();
  const sendersApi = useContext(MessageSendersContext);
  const aiUser = useContext(AiUserContext);
  const selectedUser = useContext(SelectedUserContext);
  const custom = message.metadata?.custom as MessageCustom | undefined;
  const registered = sendersApi?.senderByMessageId[message.id];
  const isDeveloper =
    custom?.senderType === "developer" ||
    (custom?.senderType == null && registered?.type === "developer");
  const isRunning = message.status?.type === "running";
  const messageText = getMessageText(message);
  const hasText = Boolean(messageText.trim());
  const isPersistedWorking = Boolean(custom?.working) && !hasText;
  const liveCreatedAtRef = useRef<string | null>(null);
  if (hasText && liveCreatedAtRef.current == null) {
    liveCreatedAtRef.current = custom?.createdAt ?? new Date().toISOString();
  }
  const createdAt = custom?.createdAt ?? liveCreatedAtRef.current;
  const messages = useThread((t) => t.messages);
  const isLatest = useMemo(
    () =>
      messages.length > 0 &&
      messages[messages.length - 1]!.id === message.id,
    [messages, message.id]
  );

  // Working rows stick under the parent user bubble.
  if (isPersistedWorking) return null;
  if (isRunning && !hasText) return null;

  const sender: MessageSenderInfo | null = isDeveloper
    ? {
        name: custom?.senderName || registered?.name || "Developer",
        type: "developer",
      }
    : aiUser
      ? {
          type: "ai",
          name:
            aiUser.name !== "ai"
              ? aiUser.name
              : custom?.senderName && custom.senderName !== "ai"
                ? custom.senderName
                : registered?.type === "ai" && registered.name !== "ai"
                  ? registered.name
                  : aiUser.name,
        }
      : registered?.type === "ai"
        ? registered
        : resolveMessageSender(message.id, custom, sendersApi);

  const side = resolveBubbleSide(
    selectedUser,
    sender,
    message.role,
    isDeveloper
  );
  const onRight = isRightAligned(side);
  const explicitParent = custom?.parentMessageId;
  const parentId =
    explicitParent ??
    (!isDeveloper ? inferParentUserId(messages, message.id) : undefined);
  const showReply =
    Boolean(explicitParent) ||
    shouldShowAsReply(messages, message.id, parentId);
  const showRefreshHint =
    side === "ai" &&
    hasText &&
    !isLikelyStatusAck(messageText) &&
    isLatest;

  return (
    <MessagePrimitive.Root
      className={`flex px-4 py-1 ${onRight ? "justify-end" : "justify-start"}`}
    >
      <div
        id={`aui-msg-${message.id}`}
        data-message-id={message.id}
        data-db-id={custom?.dbId}
        className="min-w-0 max-w-[85%]"
      >
        <MessageMeta
          name={
            sender?.name ||
            (isDeveloper ? "Dev" : undefined)
          }
          createdAt={createdAt}
          align={onRight ? "right" : "left"}
          nameClassName={
            isDeveloper && side === "other"
              ? "text-emerald-700"
              : "text-gray-400"
          }
          badge={
            isDeveloper ? (
              <SenderRoleBadge type="developer" />
            ) : side === "self" ? (
              <SenderRoleBadge type="client" />
            ) : null
          }
        />
        <div
          className={`flex min-w-0 items-center gap-1.5 ${
            onRight ? "flex-row-reverse" : ""
          }`}
        >
        <div
          className={`min-w-0 w-fit max-w-full overflow-hidden break-words rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
            side === "self"
              ? "rounded-br-sm bg-indigo-600 text-white"
              : side === "ai"
                ? "rounded-bl-sm border border-gray-200 bg-white text-gray-800"
                : isDeveloper
                  ? "rounded-bl-sm border border-emerald-200 bg-emerald-50 text-emerald-950"
                  : "rounded-bl-sm border border-gray-200 bg-white text-gray-800"
          }`}
        >
          {showReply && parentId ? (
            <ReplyQuote
              parentMessageId={parentId}
              currentSide={side}
              isDeveloper={isDeveloper}
            />
          ) : null}
          {hasText &&
            (side === "self" ? (
              <p className="aui-plain whitespace-pre-wrap break-words">
                <MentionText text={messageText} tone="user" />
              </p>
            ) : (
              <MessagePrimitive.Content components={{ Text: MarkdownText }} />
            ))}
        </div>
        {hasText && !isPersistedWorking ? (
          <ReplyAction
            messageId={message.id}
            dbId={custom?.dbId}
            name={sender?.name ?? (isDeveloper ? "Developer" : "AI")}
            snippet={messageText}
          />
        ) : null}
        </div>
        {showRefreshHint && (
          <p
            className={`mt-1.5 px-1 text-[11px] leading-snug text-gray-400 ${
              onRight ? "text-right" : "text-left"
            }`}
          >
            If you don&apos;t see the change in the app,{" "}
            <a
              href={resolveProxyAppUrl() || window.location.href}
              className="underline underline-offset-2 hover:text-gray-600"
              onClick={(e) => {
                e.preventDefault();
                refreshHostApp();
              }}
            >
              refresh
            </a>{" "}
            this page.
          </p>
        )}
      </div>
    </MessagePrimitive.Root>
  );
}

function MessageRenderer() {
  const message = useMessage();
  // Keep cancelled status acks visible; hide empty incomplete shells only.
  // Running shells stay so the progress indicator can show immediately.
  if (message.role === "assistant" && message.status?.type === "incomplete") {
    if (!getMessageText(message).trim()) return null;
  }
  if (message.role === "user") return <UserMessage />;
  return <AssistantMessage />;
}

function ComposerSendButton({ hasAttachments = false }: { hasAttachments?: boolean }) {
  const isEmpty = useComposer((c) => c.isEmpty);
  const isRunning = useThread((t) => t.isRunning);
  const messages = useThread((t) => t.messages);
  const queueLength = useContext(MessageQueueContext)?.queue.length ?? 0;
  const willQueue =
    threadHasWorkingMessage(messages, isRunning) || queueLength > 0;

  return (
    <button
      type="submit"
      disabled={isEmpty && !hasAttachments}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      aria-label={willQueue ? "Send (adds to queue)" : "Send"}
      title={
        willQueue
          ? "Send (queued until the current request finishes)"
          : "Send"
      }
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="h-5 w-5"
        aria-hidden
      >
        <path
          d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

const MessageQueueContext = createContext<{
  queue: QueuedSend[];
} | null>(null);

function chatUserStorageKey(conversationId: string): string {
  return `ai-ui:chat-auth:${conversationId}`;
}

function readStoredUserKey(
  conversationId: string,
  composableUsers: ChatUser[]
): string | null {
  if (typeof window === "undefined" || composableUsers.length === 0) {
    return null;
  }
  try {
    const stored = sessionStorage.getItem(chatUserStorageKey(conversationId));
    if (
      stored &&
      composableUsers.some((u, i) => userKey(u, i) === stored)
    ) {
      return stored;
    }
  } catch {
    // sessionStorage may be unavailable
  }
  return null;
}

function writeStoredUserKey(
  conversationId: string,
  key: string | null
): void {
  if (typeof window === "undefined") return;
  try {
    const storageKey = chatUserStorageKey(conversationId);
    if (key) sessionStorage.setItem(storageKey, key);
    else sessionStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

function ChatLoginScreen({
  title,
  className,
  googleEnabled,
  onPasswordSignIn,
  onGoogleSignIn,
}: {
  title: string;
  className?: string;
  googleEnabled: boolean;
  onPasswordSignIn: (email: string, password: string) => Promise<void>;
  onGoogleSignIn?: (popup: Window | null) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googlePending, setGooglePending] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim()) {
      setError("Enter your email");
      return;
    }
    if (!password) {
      setError("Enter your password");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onPasswordSignIn(email.trim(), password);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Sign in failed. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none ring-teal-500/30 placeholder:text-gray-400 focus:border-teal-400 focus:ring-2 disabled:bg-gray-50 disabled:text-gray-400";

  return (
    <div className={`flex h-full flex-col bg-gray-50 ${className ?? ""}`}>
      <div className="border-b border-gray-200 bg-white px-5 py-4">
        <h2 className="font-semibold text-gray-800">{title}</h2>
        <p className="text-xs text-gray-400">
          Sign in with your Maintainer Pro account
        </p>
      </div>
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-6">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <h3 className="text-base font-semibold text-gray-800">Sign in</h3>
            <p className="mt-1 text-sm text-gray-500">
              {googleEnabled
                ? "Continue with Google, or enter your email and password."
                : "Enter your email and password to continue."}
            </p>
          </div>
          {googleEnabled && onGoogleSignIn && (
            <div className="space-y-3">
              <button
                type="button"
                disabled={googlePending || submitting}
                onClick={() => {
                  const popup = openAuthPopup();
                  setGooglePending(true);
                  setError(null);
                  void onGoogleSignIn(popup)
                    .catch((err) => {
                      popup?.close();
                      setError(
                        err instanceof Error
                          ? err.message
                          : "Google sign-in failed. Try again."
                      );
                    })
                    .finally(() => setGooglePending(false));
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <GoogleMark />
                {googlePending ? "Opening Google…" : "Continue with Google"}
              </button>
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-wide text-gray-400">
                <span className="h-px flex-1 bg-gray-200" />
                <span>or</span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
            </div>
          )}
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
            <div>
              <label
                htmlFor="ai-chat-email"
                className="mb-1 block text-xs font-medium text-gray-600"
              >
                Email
              </label>
              <input
                id="ai-chat-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                disabled={submitting || googlePending}
                className={fieldClass}
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label
                htmlFor="ai-chat-password"
                className="mb-1 block text-xs font-medium text-gray-600"
              >
                Password
              </label>
              <input
                id="ai-chat-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                }}
                disabled={submitting || googlePending}
                className={fieldClass}
                placeholder="Enter password"
              />
            </div>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={!email.trim() || !password || submitting || googlePending}
              className="w-full rounded-xl bg-teal-700 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function openAuthPopup(): Window | null {
  const width = 480;
  const height = 700;
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
  const popup = window.open(
    "about:blank",
    "mp-google-chat",
    `popup=yes,width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,status=no`
  );
  if (popup) {
    try {
      popup.document.write(
        `<!DOCTYPE html><html><head><title>Google sign-in</title></head><body style="font-family:system-ui,sans-serif;padding:2rem;color:#1f2937"><p>Opening Google…</p></body></html>`
      );
      popup.document.close();
      popup.focus();
    } catch {
      /* ignore */
    }
  }
  return popup;
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

function ComposerMentionInput({
  users,
  excludeUser,
  onPaste,
  onSubmitKeyDown,
}: {
  users: ChatUser[];
  /** Currently selected sender — cannot @tag yourself. */
  excludeUser?: ChatUser | null;
  onPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  onSubmitKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}) {
  const composerRuntime = useComposerRuntime();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [mention, setMention] = useState<{
    start: number;
    query: string;
  } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const filtered = useMemo(() => {
    if (!mention || users.length === 0) return [];
    const q = mention.query.toLowerCase();
    return users.filter((u) => {
      if (
        excludeUser &&
        u.type === excludeUser.type &&
        u.name.toLowerCase() === excludeUser.name.toLowerCase()
      ) {
        return false;
      }
      return (
        u.name.toLowerCase().includes(q) ||
        u.type.toLowerCase().includes(q) ||
        (u.role?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [mention, users, excludeUser]);

  useEffect(() => {
    setActiveIndex(0);
  }, [mention?.start, mention?.query, filtered.length]);

  const syncMention = (textarea: HTMLTextAreaElement) => {
    setMention(getActiveMention(textarea.value, textarea.selectionStart));
  };

  const insertMention = (user: ChatUser) => {
    if (!mention) return;
    const textarea = wrapRef.current?.querySelector("textarea");
    const text = textarea?.value ?? composerRuntime.getState().text;
    const cursor = textarea?.selectionStart ?? text.length;
    const before = text.slice(0, mention.start);
    const after = text.slice(cursor);
    const inserted = `@${user.name} `;
    const next = `${before}${inserted}${after}`;
    composerRuntime.setText(next);
    setMention(null);
    requestAnimationFrame(() => {
      const el = wrapRef.current?.querySelector("textarea");
      if (!el) return;
      const pos = before.length + inserted.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && filtered.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % filtered.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        insertMention(filtered[activeIndex] ?? filtered[0]!);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMention(null);
        return;
      }
    }
    onSubmitKeyDown(event);
  };

  return (
    <div ref={wrapRef} className="relative flex min-w-0 flex-1 items-end">
      <ComposerPrimitive.Input
        submitMode="none"
        placeholder={
          users.length > 0
            ? "Message… (@ to tag someone)"
            : "Message… (paste a screenshot or attach a file)"
        }
        className="box-border block min-h-10 max-h-32 w-full resize-none rounded-lg border-0 bg-transparent px-2 py-2 text-sm leading-5 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-0"
        rows={1}
        onPaste={onPaste}
        onKeyDown={handleKeyDown}
        onKeyUp={(event) => syncMention(event.currentTarget)}
        onClick={(event) => syncMention(event.currentTarget)}
      />
      {mention && filtered.length > 0 && (
        <div
          role="listbox"
          aria-label="Tag user"
          className="absolute bottom-full left-0 z-20 mb-1.5 max-h-48 min-w-[12rem] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          <p className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Tag someone
          </p>
          {filtered.map((user, index) => {
            const active = index === activeIndex;
            return (
              <button
                key={`${user.type}:${user.name}:${index}`}
                type="button"
                role="option"
                aria-selected={active}
                onMouseDown={(event) => {
                  event.preventDefault();
                  insertMention(user);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors ${
                  active
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="font-medium">@{user.name}</span>
                <span
                  className={`text-[10px] capitalize ${
                    active ? "text-indigo-500" : "text-gray-400"
                  }`}
                >
                  {userBadge(user)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type ExportedThread = ReturnType<
  ReturnType<typeof useThreadRuntime>["export"]
>;
type ExportedThreadMessage = ExportedThread["messages"][number];

/**
 * assistant-ui startRun needs the parent at the branch head. Stash later
 * bubbles (queued user messages, etc.), run, then restore so the queue UI
 * is not wiped.
 */
function stashMessagesAfter(
  threadRuntime: ReturnType<typeof useThreadRuntime>,
  messageId: string
): ExportedThreadMessage[] {
  const exported = threadRuntime.export();
  const idx = exported.messages.findIndex((m) => m.message.id === messageId);
  if (idx < 0 || idx === exported.messages.length - 1) {
    if (idx >= 0 && exported.headId !== messageId) {
      threadRuntime.import({ ...exported, headId: messageId });
    }
    return [];
  }

  const later = exported.messages.slice(idx + 1).filter((entry) => {
    const m = entry.message;
    if (m.role !== "assistant") return true;
    const text =
      typeof m.content === "string"
        ? m.content
        : Array.isArray(m.content)
          ? m.content
              .map((p) =>
                p && typeof p === "object" && "text" in p
                  ? String((p as { text?: string }).text ?? "")
                  : ""
              )
              .join("")
          : "";
    if (text.trim()) return true;
    const meta = m.metadata?.custom as MessageCustom | undefined;
    if (meta?.working) return false;
    const status = m.status?.type;
    // Drop empty running/incomplete shells — a fresh run will recreate them.
    return status !== "running" && status !== "incomplete";
  });

  threadRuntime.import({
    headId: messageId,
    messages: exported.messages.slice(0, idx + 1),
  });
  return later;
}

function restoreStashedMessages(
  threadRuntime: ReturnType<typeof useThreadRuntime>,
  stashed: ExportedThreadMessage[]
): void {
  if (stashed.length === 0) return;
  const exported = threadRuntime.export();
  const merged = [...exported.messages, ...stashed];
  const lastId = merged[merged.length - 1]?.message.id ?? exported.headId;
  threadRuntime.import({
    headId: lastId,
    messages: merged,
  });
}

function localMessageIds(message: {
  id: string;
  metadata?: { custom?: unknown };
}): string[] {
  const custom = message.metadata?.custom as MessageCustom | undefined;
  return [message.id, custom?.dbId].filter((id): id is string => Boolean(id));
}

function findLocalByRemoteId(
  threadRuntime: ReturnType<typeof useThreadRuntime>,
  remoteId?: string | null
) {
  if (!remoteId) return undefined;
  return threadRuntime
    .getState()
    .messages.find((m) => localMessageIds(m).includes(remoteId));
}

function patchMessageCustom(
  threadRuntime: ReturnType<typeof useThreadRuntime>,
  match: { localId?: string; dbId?: string },
  patch: Partial<MessageCustom>
) {
  const exported = threadRuntime.export();
  let changed = false;
  const messages = exported.messages.map((entry) => {
    const custom = (entry.message.metadata?.custom ?? {}) as MessageCustom;
    const hit =
      (match.localId && entry.message.id === match.localId) ||
      (match.dbId &&
        (custom.dbId === match.dbId || entry.message.id === match.dbId));
    if (!hit) return entry;
    changed = true;
    return {
      ...entry,
      message: {
        ...entry.message,
        metadata: {
          ...entry.message.metadata,
          custom: { ...custom, ...patch },
        },
      },
    };
  });
  if (!changed) return;
  threadRuntime.import({ headId: exported.headId, messages });
}

/**
 * Match Maintainer Pro admin: flat chronological list.
 * Live events upsert-by-id or append — never splice under parentMessageId
 * (that creates assistant-ui sibling branches / "next to source" quotes).
 */
function replaceThreadLinear(
  threadRuntime: ReturnType<typeof useThreadRuntime>,
  linear: ThreadMessage[]
) {
  // Local runtime has no history.delete adapter, so deleteMessage() always
  // rejects ("Runtime does not support deleting messages"). import() already
  // replaces the repository with this list.
  threadRuntime.import({
    headId: linear.at(-1)?.id ?? null,
    messages: linear.map((message, i) => ({
      parentId: i === 0 ? null : linear[i - 1]!.id,
      message,
    })),
  });
}

/** Same as admin `upsertLiveMessage` — update in place or append. */
function upsertRemoteLinear(
  threadRuntime: ReturnType<typeof useThreadRuntime>,
  message: ThreadMessage
) {
  const linear = [...threadRuntime.getState().messages];
  const idx = linear.findIndex(
    (row) =>
      row.id === message.id ||
      threadMessageMatchesId(row, message.id) ||
      ((message.metadata?.custom as MessageCustom | undefined)?.dbId &&
        localMessageIds(row).includes(
          (message.metadata?.custom as MessageCustom).dbId!
        ))
  );
  if (idx >= 0) {
    const prev = linear[idx]!;
    const prevCustom = (prev.metadata?.custom ?? {}) as MessageCustom;
    const nextCustom = (message.metadata?.custom ?? {}) as MessageCustom;
    linear[idx] = {
      ...prev,
      ...message,
      id: prev.id,
      metadata: {
        ...prev.metadata,
        ...message.metadata,
        custom: { ...prevCustom, ...nextCustom, dbId: nextCustom.dbId ?? prevCustom.dbId },
      },
    } as ThreadMessage;
  } else {
    linear.push(message);
  }
  replaceThreadLinear(threadRuntime, linear);
}

function dropWorkingPlaceholders(
  threadRuntime: ReturnType<typeof useThreadRuntime>,
  ids?: string[]
) {
  const idSet = ids?.length ? new Set(ids) : null;
  if (!idSet) {
    // Admin waits for message.deleted for WIP rows; only drop by id here.
    return;
  }
  const linear = threadRuntime
    .getState()
    .messages.filter((m) => {
      const custom = (m.metadata?.custom ?? {}) as MessageCustom;
      if (idSet.has(m.id) || (custom.dbId && idSet.has(custom.dbId))) {
        return false;
      }
      return true;
    });
  if (linear.length === threadRuntime.getState().messages.length) return;
  replaceThreadLinear(threadRuntime, linear);
}

function queuedRankFromThread(
  messages: readonly ThreadMessage[],
  messageId: string,
  dbId?: string
): number | null {
  const waiting = messages
    .filter((m) => {
      if (m.role !== "user") return false;
      const meta = m.metadata?.custom as MessageCustom | undefined;
      return meta?.queueStatus === "queued";
    })
    .sort((a, b) => {
      const ac =
        (a.metadata?.custom as MessageCustom | undefined)?.createdAt ?? "";
      const bc =
        (b.metadata?.custom as MessageCustom | undefined)?.createdAt ?? "";
      const byTime = ac.localeCompare(bc);
      return byTime !== 0 ? byTime : a.id.localeCompare(b.id);
    });
  const idx = waiting.findIndex((m) => {
    const meta = m.metadata?.custom as MessageCustom | undefined;
    return (
      m.id === messageId ||
      (dbId && meta?.dbId === dbId) ||
      (meta?.dbId && meta.dbId === messageId)
    );
  });
  return idx >= 0 ? idx + 1 : null;
}

function threadHasWorkingMessage(
  messages: ThreadMessage[],
  isRunning: boolean
): boolean {
  if (isRunning) return true;
  for (const m of messages) {
    const meta = m.metadata?.custom as MessageCustom | undefined;
    if (meta?.queueStatus === "working" || meta?.working) return true;
  }
  return false;
}

function userTurnAlreadyReplied(
  messages: readonly ThreadMessage[],
  item: Pick<QueuedSend, "id" | "text">
): boolean {
  let idx = messages.findIndex((m) => m.id === item.id);
  if (idx < 0) {
    const needle = item.text.trim();
    idx = messages.findIndex(
      (m) => m.role === "user" && getMessageText(m).trim() === needle
    );
  }
  if (idx < 0) return false;
  for (let i = idx + 1; i < messages.length; i++) {
    const m = messages[i]!;
    if (m.role === "user") break;
    const meta = m.metadata?.custom as MessageCustom | undefined;
    if (meta?.working) continue;
    if (m.role === "assistant" && getMessageText(m).trim()) return true;
  }
  return false;
}

function useQueuedSubmit(options: {
  getSelectedUser: () => ChatUser | null;
  /** When true, refuse to send until a user is signed in. */
  requireLogin: boolean;
  apiUrl: string;
  conversationId?: string;
  registerSender: (messageId: string, sender: MessageSenderInfo) => void;
  registerImages: (
    messageId: string,
    items: Array<string | AttachmentView>
  ) => void;
  takePending: () => PendingImage[];
  hasPendingAttachments?: () => boolean;
  pushSendBatch: (batch: SendBatch) => void;
  queue: QueuedSend[];
  setQueue: (queue: QueuedSend[]) => void;
  onQueuedNextRef?: { current: ((next: { id: string; content: string }) => void) | null };
  chatSocket?: ChatSocket | null;
  aiServerSocket?: ChatSocket | null;
  maintainerProUrl?: string;
  maintainerProApiKey?: string;
  replyTargetRef?: { current: ReplyTarget | null };
  clearReplyTarget?: () => void;
  buildContext?: () => ClientContext;
}) {
  const threadRuntime = useThreadRuntime();
  const composerRuntime = useComposerRuntime();
  const optsRef = useRef(options);
  optsRef.current = options;
  const queueRef = useRef(options.queue);
  queueRef.current = options.queue;
  const stashedAfterRunRef = useRef<ExportedThreadMessage[]>([]);
  const startedDbIdsRef = useRef(new Set<string>());
  const pendingTurnRef = useRef<{
    localId?: string;
    dbId: string;
    text: string;
    images?: PendingImage[];
    sender?: ChatUser | null;
    createdAt?: string;
  } | null>(null);
  const pendingByRequestRef = useRef(
    new Map<
      string,
      {
        localId: string;
        text: string;
        images: PendingImage[];
        sender: ChatUser | null;
        createdAt: string;
      }
    >()
  );

  const appendUserBubble = useCallback(
    (item: Omit<QueuedSend, "id"> & { id?: string }) => {
      const { registerSender, registerImages } = optsRef.current;
      const localId = item.id ?? newMessageId();
      const createdAt = item.createdAt ?? new Date().toISOString();
      const custom = {
        ...(item.sender
          ? {
              senderType: persistSenderType(item.sender) ?? "client",
              senderName: item.sender.name,
            }
          : {}),
        createdAt,
        ...(item.status ? { queueStatus: item.status } : {}),
        dbId: item.dbId ?? localId,
        ...(item.parentMessageId ? { parentMessageId: item.parentMessageId } : {}),
      };
      // append() is async (waits on runtime init), so getState() right after
      // often misses the new row. Import the bubble so persist can continue.
      const exported = threadRuntime.export();
      const parentId =
        exported.headId ??
        exported.messages.at(-1)?.message.id ??
        null;
      threadRuntime.import({
        headId: localId,
        messages: [
          ...exported.messages,
          {
            parentId,
            message: {
              id: localId,
              role: "user",
              content: [{ type: "text", text: item.text }],
              createdAt: new Date(createdAt),
              attachments: [],
              metadata: { custom },
              status: { type: "complete", reason: "unknown" },
            },
          },
        ],
      });
      const added =
        threadRuntime.getState().messages.find((m) => m.id === localId) ??
        [...threadRuntime.getState().messages]
          .reverse()
          .find((m) => getMessageText(m).trim() === item.text.trim());
      const id = added?.id ?? localId;
      sendLog.info(
        {
          localId: id,
          imported: Boolean(added),
          parentId,
          preview: previewText(item.text),
        },
        "appendUserBubble"
      );
      if (item.sender) {
        registerSender(id, {
          name: item.sender.name,
          type: persistSenderType(item.sender) ?? "client",
        });
      }
      if (item.images.length > 0) {
        registerImages(
          id,
          item.images.map((img) => ({
            url: img.dataUrl,
            name: img.name,
            mimeType: img.mimeType,
          }))
        );
      }
      return id;
    },
    [threadRuntime]
  );

  const runWorking = useCallback(
    (input: {
      localId?: string;
      dbId: string;
      text: string;
      images?: PendingImage[];
      sender?: ChatUser | null;
      createdAt?: string;
    }) => {
      if (!input.dbId || startedDbIdsRef.current.has(input.dbId)) return;
      if (threadRuntime.getState().isRunning) {
        pendingTurnRef.current = input;
        return;
      }
      startedDbIdsRef.current.add(input.dbId);
      const createdAt = input.createdAt ?? new Date().toISOString();
      patchMessageCustom(
        threadRuntime,
        { localId: input.localId, dbId: input.dbId },
        { queueStatus: "working", dbId: input.dbId, createdAt }
      );
      optsRef.current.pushSendBatch({
        images: input.images ?? [],
        sender: input.sender ?? null,
        skipPersistUser: true,
        userMessageId: input.dbId,
      });
      const msgs = threadRuntime.getState().messages;
      const existing = msgs.find(
        (m) =>
          m.id === input.localId ||
          m.id === input.dbId ||
          (m.metadata?.custom as MessageCustom | undefined)?.dbId ===
            input.dbId
      );
      const parentId = existing?.id ?? input.localId;
      if (parentId && msgs.some((m) => m.id === parentId)) {
        stashedAfterRunRef.current = stashMessagesAfter(
          threadRuntime,
          parentId
        );
        threadRuntime.startRun({ parentId });
        return;
      }
      const id = appendUserBubble({
        id: input.localId,
        dbId: input.dbId,
        text: input.text,
        images: input.images ?? [],
        sender: input.sender ?? null,
        status: "working",
        createdAt,
      });
      if (!id) return;
      stashedAfterRunRef.current = stashMessagesAfter(threadRuntime, id);
      threadRuntime.startRun({ parentId: id });
    },
    [appendUserBubble, threadRuntime]
  );

  useEffect(() => {
    let wasRunning = threadRuntime.getState().isRunning;
    return threadRuntime.subscribe(() => {
      const running = threadRuntime.getState().isRunning;
      if (wasRunning && !running) {
        const stashed = stashedAfterRunRef.current;
        stashedAfterRunRef.current = [];
        if (stashed.length > 0) {
          restoreStashedMessages(threadRuntime, stashed);
        }
        const pending = pendingTurnRef.current;
        if (pending && !startedDbIdsRef.current.has(pending.dbId)) {
          pendingTurnRef.current = null;
          runWorking(pending);
        }
      }
      wasRunning = running;
    });
  }, [runWorking, threadRuntime]);

  useEffect(() => {
    if (options.onQueuedNextRef) {
      options.onQueuedNextRef.current = (next) => {
        const queued = queueRef.current.find(
          (item) => item.dbId === next.id || item.id === next.id
        );
        const msgs = threadRuntime.getState().messages;
        const fromThread = msgs.find(
          (m) =>
            m.id === next.id ||
            (m.metadata?.custom as MessageCustom | undefined)?.dbId === next.id
        );
        if (queued) {
          queueRef.current = queueRef.current.filter((item) => item !== queued);
          optsRef.current.setQueue(queueRef.current);
        }
        if (
          optsRef.current.maintainerProUrl &&
          optsRef.current.maintainerProApiKey
        ) {
          return;
        }
        runWorking({
          localId: fromThread?.id ?? queued?.id,
          dbId: next.id,
          text: next.content || queued?.text || "",
          images: queued?.images,
          sender: queued?.sender,
          createdAt: queued?.createdAt,
        });
      };
      return () => {
        if (options.onQueuedNextRef) options.onQueuedNextRef.current = null;
      };
    }
  }, [options.onQueuedNextRef, runWorking, threadRuntime]);

  const bindSaved = useCallback(
    (localId: string, saved: { id: string; queueStatus?: string | null; createdAt?: string }) => {
      const status =
        saved.queueStatus === "queued" || saved.queueStatus === "working"
          ? saved.queueStatus
          : null;
      patchMessageCustom(
        threadRuntime,
        { localId, dbId: saved.id },
        {
          dbId: saved.id,
          ...(status ? { queueStatus: status } : {}),
          ...(saved.createdAt ? { createdAt: saved.createdAt } : {}),
        }
      );
      if (status === "queued") {
        const pending = [...pendingByRequestRef.current.values()].find(
          (p) => p.localId === localId
        );
        const item: QueuedSend = {
          id: localId,
          dbId: saved.id,
          text: pending?.text ?? "",
          images: pending?.images ?? [],
          sender: pending?.sender ?? null,
          status: "queued",
          createdAt: saved.createdAt ?? pending?.createdAt ?? new Date().toISOString(),
        };
        const next = [
          ...queueRef.current.filter(
            (q) => q.id !== item.id && q.dbId !== item.dbId
          ),
          item,
        ];
        queueRef.current = next;
        optsRef.current.setQueue(next);
      }
    },
    [threadRuntime]
  );

  const promotingRef = useRef(false);
  const promoteNextQueued = useCallback((opts?: {
    assumeWorkingDone?: boolean;
    /** Only force-complete this parent (AI reply's parentMessageId). */
    completedParentId?: string | null;
  }) => {
    const {
      maintainerProUrl,
      maintainerProApiKey,
      conversationId,
    } = optsRef.current;
    // Maintainer Pro: admin owns enqueue/claim/promote. Client only syncs labels.
    if (maintainerProUrl && maintainerProApiKey && conversationId) {
      const msgs = threadRuntime.getState().messages;
      const completedParentId = opts?.completedParentId?.trim() || null;
      if (completedParentId) {
        patchMessageCustom(
          threadRuntime,
          { dbId: completedParentId },
          { dbId: completedParentId, queueStatus: null }
        );
      } else if (opts?.assumeWorkingDone) {
        const working = msgs.find((m) => {
          if (m.role !== "user") return false;
          const meta = m.metadata?.custom as MessageCustom | undefined;
          return meta?.queueStatus === "working";
        });
        if (
          working &&
          userTurnAlreadyReplied(msgs, {
            id: working.id,
            text: getMessageText(working),
          })
        ) {
          const dbId =
            (working.metadata?.custom as MessageCustom | undefined)?.dbId ??
            working.id;
          patchMessageCustom(
            threadRuntime,
            { localId: working.id, dbId },
            { dbId, queueStatus: null }
          );
        }
      }
      log.debug(
        { conversationId, completedParentId },
        "promote skipped (server-owned queue)"
      );
      return;
    }
    // Standalone embeds: adapter nextQueued / local-store handle promotion.
  }, [threadRuntime]);

  const persistUser = useCallback(
    async (input: {
      requestId: string;
      localId: string;
      text: string;
      images: PendingImage[];
      sender: ChatUser | null;
      createdAt: string;
      queueStatus: "working" | "queued";
      parentMessageId?: string;
    }) => {
      const {
        conversationId,
        apiUrl,
        chatSocket,
        aiServerSocket,
        maintainerProUrl,
        maintainerProApiKey,
        buildContext,
      } = optsRef.current;
      let context: ClientContext | undefined;
      try {
        context = buildContext?.();
      } catch {
        try {
          context = buildBrowserClientContext();
        } catch {
          context = undefined;
        }
      }
      let attachmentIds: string[] = [];
      if (
        input.images.length &&
        maintainerProUrl &&
        maintainerProApiKey &&
        conversationId
      ) {
        const mp = createMaintainerProClient({
          baseUrl: maintainerProUrl,
          apiKey: maintainerProApiKey,
        });
        for (const img of input.images) {
          const att = await mp.uploadAttachment(conversationId, {
            name: img.name,
            mimeType: img.mimeType,
            data: img.base64,
          });
          attachmentIds.push(att.id);
        }
      }
      const payload = {
        type: "chat.send",
        requestId: input.requestId,
        id: input.localId,
        conversationId,
        content: input.text,
        senderName: input.sender?.name,
        senderType: persistSenderType(input.sender),
        attachmentIds,
        parentMessageId: input.parentMessageId,
        context,
      };
      sendLog.info(
        {
          requestId: input.requestId,
          localId: input.localId,
          conversationId: conversationId ?? null,
          optimisticStatus: input.queueStatus,
          attachments: attachmentIds.length,
          pageUrl: context?.pageUrl ?? null,
          route: context?.route ?? null,
          preview: previewText(input.text),
          mpConfigured: Boolean(maintainerProUrl && maintainerProApiKey),
          mpSocketOpen: Boolean(chatSocket?.isOpen()),
        },
        "persistUser start"
      );
      if (maintainerProUrl && maintainerProApiKey && conversationId) {
        try {
          const mp = createMaintainerProClient({
            baseUrl: maintainerProUrl,
            apiKey: maintainerProApiKey,
          });
          const saved = await mp.sendUserMessage({
            conversationId,
            id: input.localId,
            content: input.text,
            senderType: persistSenderType(input.sender),
            senderName: input.sender?.name,
            attachmentIds,
            parentMessageId: input.parentMessageId,
            context,
          });
          sendLog.info(
            { id: saved.id, queueStatus: saved.queueStatus },
            "persistUser mp http ok (server owns queue)"
          );
          pendingByRequestRef.current.delete(input.requestId);
          bindSaved(input.localId, saved);
          // Server enqueue assigns working/queued and dispatches chat.turn.
          return;
        } catch (httpErr) {
          sendLog.warn({ err: httpErr }, "persistUser mp http failed; trying WebSocket");
        }
        try {
          if (!chatSocket) throw new Error("Chat WebSocket is not ready");
          await chatSocket.waitUntilOpen();
          sendLog.info({ requestId: input.requestId }, "persistUser via mp websocket");
          const saved = await chatSocket.request(payload);
          pendingByRequestRef.current.delete(input.requestId);
          if (typeof saved.id === "string") {
            bindSaved(input.localId, {
              id: saved.id,
              queueStatus:
                saved.queueStatus === "queued" ||
                saved.queueStatus === "working"
                  ? saved.queueStatus
                  : input.queueStatus,
              createdAt:
                typeof saved.createdAt === "string"
                  ? saved.createdAt
                  : undefined,
            });
          }
          return;
        } catch (wsErr) {
          sendLog.error({ err: wsErr }, "persistUser mp websocket failed");
          throw new Error("Failed to save message");
        }
      }
      if (conversationId && chatSocket?.isOpen() && chatSocket.send(payload)) {
        sendLog.info({ conversationId }, "persistUser fire-and-forget chat.send");
        return;
      }
      const saved = await persistUserMessage(
        apiUrl,
        conversationId,
        input.text,
        input.sender,
        input.images,
        input.queueStatus,
        input.localId,
        input.parentMessageId
      );
      pendingByRequestRef.current.delete(input.requestId);
      if (saved?.id) {
        bindSaved(input.localId, {
          id: saved.id,
          queueStatus: saved.queueStatus,
        });
        if (saved.queueStatus !== "queued") {
          const ranOnWs = await sendAiChatRun(aiServerSocket, {
            conversationId,
            userMessage: input.text,
            userMessageId: saved.id,
            sender: input.sender,
            context,
          });
          sendLog.info(
            { ranOnWs, savedId: saved.id, queueStatus: saved.queueStatus },
            "persistUser local save follow-up"
          );
          if (!ranOnWs && !chatSocket?.isOpen()) {
            sendLog.info({ savedId: saved.id }, "persistUser → runWorking (http adapter)");
            runWorking({
              localId: input.localId,
              dbId: saved.id,
              text: input.text,
              images: input.images,
              sender: input.sender,
              createdAt: input.createdAt,
            });
          }
        }
      }
    },
    [bindSaved, runWorking]
  );

  useEffect(() => {
    const sockets = [options.chatSocket, options.aiServerSocket].filter(
      (s): s is ChatSocket => Boolean(s)
    );
    if (sockets.length === 0) return;
    const unsubs = sockets.map((socket) =>
      socket.subscribe((msg) => {
      if (msg.type === "chat.accepted") {
        // Only handle once (admin socket is the source of truth for accept).
        if (socket !== options.chatSocket) return;
        const requestId =
          typeof msg.requestId === "string" ? msg.requestId : "";
        const message = msg.message as
          | {
              id?: string;
              queueStatus?: string | null;
              createdAt?: string;
            }
          | undefined;
        const pending = requestId
          ? pendingByRequestRef.current.get(requestId)
          : undefined;
        if (requestId) pendingByRequestRef.current.delete(requestId);
        if (!message?.id || !pending) return;
        bindSaved(pending.localId, {
          id: message.id,
          queueStatus: message.queueStatus,
          createdAt: message.createdAt,
        });
        return;
      }
      if (msg.type === "message.updated") {
        if (socket !== options.chatSocket) return;
        const message = msg.message as
          | {
              id?: string;
              role?: string;
              queueStatus?: string | null;
              createdAt?: string;
            }
          | undefined;
        if (!message?.id || (message.role && message.role !== "user")) return;
        const queued = queueRef.current.find(
          (item) => item.dbId === message.id || item.id === message.id
        );
        patchMessageCustom(
          threadRuntime,
          { localId: queued?.id, dbId: message.id },
          {
            dbId: message.id,
            queueStatus:
              message.queueStatus === "queued" ||
              message.queueStatus === "working"
                ? message.queueStatus
                : null,
            ...(message.createdAt ? { createdAt: message.createdAt } : {}),
          }
        );
        return;
      }
      if (msg.type === "message.created") {
        const created = msg.message as
          | {
              id?: string;
              role?: string;
              provider?: string | null;
              senderType?: string | null;
              content?: string;
              parentMessageId?: string | null;
            }
          | undefined;
        if (
          created?.role === "assistant" &&
          created.provider !== "working" &&
          created.senderType !== "developer" &&
          String(created.content ?? "").trim()
        ) {
          const parentId =
            typeof created.parentMessageId === "string"
              ? created.parentMessageId
              : null;
          log.debug(
            {
              preview: previewText(created.content),
              parentMessageId: parentId,
              via: socket === options.chatSocket ? "admin" : "ai-server",
            },
            "message.created ai reply"
          );
          // Maintainer Pro: admin complete-turn already promotes the next
          // queued message. Client-side promote raced and started a 2nd turn.
          if (
            optsRef.current.maintainerProUrl &&
            optsRef.current.maintainerProApiKey
          ) {
            if (parentId) {
              patchMessageCustom(
                threadRuntime,
                { dbId: parentId },
                { dbId: parentId, queueStatus: null }
              );
              queueRef.current = queueRef.current.filter(
                (item) => item.dbId !== parentId && item.id !== parentId
              );
              optsRef.current.setQueue(queueRef.current);
            }
            return;
          }
          promoteNextQueued({
            assumeWorkingDone: true,
            completedParentId: parentId,
          });
        }
        return;
      }
      if (socket !== options.chatSocket) return;
      if (msg.type === "chat.turn") {
        const message = msg.message as
          | { id?: string; content?: string; createdAt?: string }
          | undefined;
        if (!message?.id) return;
        log.debug(
          {
            messageId: message.id,
            dispatched: msg.dispatched,
            preview: previewText(message.content),
          },
          "chat.turn"
        );
        const queued = queueRef.current.find(
          (item) => item.dbId === message.id || item.id === message.id
        );
        const pending = [...pendingByRequestRef.current.values()].find(
          (p) => p.localId === queued?.id
        );
        if (queued) {
          queueRef.current = queueRef.current.filter((item) => item !== queued);
          optsRef.current.setQueue(queueRef.current);
        }
        const createdAt = queued?.createdAt ?? message.createdAt;
        patchMessageCustom(
          threadRuntime,
          { localId: queued?.id ?? pending?.localId, dbId: message.id },
          {
            dbId: message.id,
            queueStatus: "working",
            ...(createdAt ? { createdAt } : {}),
          }
        );
        const coordinated = Boolean(
          optsRef.current.maintainerProUrl &&
            optsRef.current.maintainerProApiKey
        );
        if (coordinated) {
          // Server/ai-server already claimed this turn via chat.turn / onWorkingTurn.
          return;
        }
        runWorking({
          localId: queued?.id ?? pending?.localId,
          dbId: message.id,
          text: queued?.text ?? pending?.text ?? String(message.content ?? ""),
          images: queued?.images ?? pending?.images,
          sender: queued?.sender ?? pending?.sender,
          createdAt,
        });
        return;
      }
      if (msg.type === "chat.run.failed") {
        const userMessageId =
          typeof msg.userMessageId === "string" ? msg.userMessageId : "";
        if (!userMessageId) return;
        if (
          optsRef.current.maintainerProUrl &&
          optsRef.current.maintainerProApiKey
        ) {
          // ai-server will recover / retry via recover-queue; don't start a parallel run.
          return;
        }
        const queued = queueRef.current.find(
          (item) => item.dbId === userMessageId || item.id === userMessageId
        );
        const pending = [...pendingByRequestRef.current.values()].find(
          (p) => p.localId === queued?.id
        );
        const fromThread = threadRuntime.getState().messages.find(
          (m) =>
            m.id === userMessageId ||
            (m.metadata?.custom as MessageCustom | undefined)?.dbId ===
              userMessageId
        );
        runWorking({
          localId: fromThread?.id ?? queued?.id ?? pending?.localId,
          dbId: userMessageId,
          text:
            queued?.text ??
            pending?.text ??
            (fromThread ? getMessageText(fromThread) : ""),
          images: queued?.images ?? pending?.images,
          sender: queued?.sender ?? pending?.sender,
          createdAt: queued?.createdAt ?? pending?.createdAt,
        });
      }
    })
    );
    return () => {
      for (const unsub of unsubs) unsub();
    };
  }, [
    bindSaved,
    options.chatSocket,
    options.aiServerSocket,
    promoteNextQueued,
    runWorking,
    threadRuntime,
  ]);

  const resumedWorkingRef = useRef(false);
  useEffect(() => {
    if (resumedWorkingRef.current) return;
    const msgs = threadRuntime.getState().messages;
    if (threadRuntime.getState().isRunning) return;
    const working = msgs.find((m) => {
      if (m.role !== "user") return false;
      const meta = m.metadata?.custom as MessageCustom | undefined;
      return (
        meta?.queueStatus === "working" &&
        !userTurnAlreadyReplied(msgs, { id: m.id, text: getMessageText(m) })
      );
    });
    if (!working) return;
    resumedWorkingRef.current = true;
    if (
      optsRef.current.maintainerProUrl &&
      optsRef.current.maintainerProApiKey
    ) {
      return;
    }
    const meta = working.metadata?.custom as MessageCustom | undefined;
    runWorking({
      localId: working.id,
      dbId: meta?.dbId ?? working.id,
      text: getMessageText(working),
      createdAt: meta?.createdAt,
    });
  }, [runWorking, threadRuntime]);

  // Recover stuck "working" labels when a reply already exists (e.g. after reload).
  const clearedStuckWorkingRef = useRef(false);
  useEffect(() => {
    if (clearedStuckWorkingRef.current) return;
    const msgs = threadRuntime.getState().messages;
    const working = msgs.find((m) => {
      if (m.role !== "user") return false;
      const meta = m.metadata?.custom as MessageCustom | undefined;
      return meta?.queueStatus === "working";
    });
    const hasQueued = msgs.some((m) => {
      if (m.role !== "user") return false;
      const meta = m.metadata?.custom as MessageCustom | undefined;
      return meta?.queueStatus === "queued";
    });
    if (!working && !hasQueued) return;

    const {
      maintainerProUrl,
      maintainerProApiKey,
      conversationId,
      aiServerSocket,
    } = optsRef.current;

    if (maintainerProUrl && maintainerProApiKey && conversationId) {
      clearedStuckWorkingRef.current = true;
      const mp = createMaintainerProClient({
        baseUrl: maintainerProUrl,
        apiKey: maintainerProApiKey,
      });
      void mp
        .recoverQueue(conversationId)
        .then((result) => {
          log.info({ ...result }, "boot recoverQueue");
        })
        .catch((err) => log.warn({ err }, "boot recoverQueue failed"));
      return;
    }

    if (!working) return;
    if (
      !userTurnAlreadyReplied(msgs, {
        id: working.id,
        text: getMessageText(working),
      })
    ) {
      return;
    }
    clearedStuckWorkingRef.current = true;
    promoteNextQueued({ assumeWorkingDone: true });
  }, [promoteNextQueued, threadRuntime]);

  // Periodic recover for stuck working/queued turns (Maintainer Pro).
  useEffect(() => {
    const {
      maintainerProUrl,
      maintainerProApiKey,
      conversationId,
    } = optsRef.current;
    if (!maintainerProUrl || !maintainerProApiKey || !conversationId) return;

    let cancelled = false;
    const runRecover = async (reason: string) => {
      const {
        conversationId: cid,
        maintainerProUrl: url,
        maintainerProApiKey: key,
      } = optsRef.current;
      if (!cid || !url || !key || cancelled) return;
      const msgs = threadRuntime.getState().messages;
      const busy = msgs.some((m) => {
        const meta = m.metadata?.custom as MessageCustom | undefined;
        return (
          meta?.queueStatus === "working" ||
          meta?.queueStatus === "queued" ||
          meta?.working
        );
      });
      if (!busy) return;
      try {
        const mp = createMaintainerProClient({ baseUrl: url, apiKey: key });
        const result = await mp.recoverQueue(cid);
        if (cancelled || !result || result.action === "noop") return;
        log.info({ reason, ...result }, "recoverQueue");
      } catch (err) {
        log.warn({ err, reason }, "recoverQueue failed");
      }
    };

    const timer = window.setInterval(() => {
      void runRecover("interval");
    }, 25_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    options.conversationId,
    options.maintainerProApiKey,
    options.maintainerProUrl,
    threadRuntime,
  ]);

  return useCallback(() => {
    const composer = composerRuntime.getState();
    const text = composer.text.trim();
    const hasFiles = Boolean(optsRef.current.hasPendingAttachments?.());
    if (!text && composer.isEmpty && !hasFiles) {
      sendLog.warn({}, "submit ignored (empty composer)");
      return;
    }

    const {
      getSelectedUser,
      requireLogin,
      conversationId,
      takePending,
      setQueue,
      chatSocket,
      aiServerSocket,
    } = optsRef.current;
    const selectedUser = getSelectedUser();

    sendLog.info(
      {
        conversationId: conversationId ?? null,
        sender: selectedUser
          ? { name: selectedUser.name, type: selectedUser.type }
          : null,
        requireLogin,
        preview: previewText(text),
        mpSocketOpen: Boolean(chatSocket?.isOpen()),
        aiServerOpen: Boolean(aiServerSocket?.isOpen()),
      },
      "submit"
    );

    if (requireLogin && !selectedUser) {
      sendLog.warn({}, "submit blocked (login required)");
      return;
    }

    const replyTo = optsRef.current.replyTargetRef?.current ?? null;
    const parentMessageId = replyTo?.id;
    optsRef.current.clearReplyTarget?.();

    const thread = threadRuntime.getState();
    const images = takePending();
    const createdAt = new Date().toISOString();
    const status: "working" | "queued" = threadHasWorkingMessage(
      thread.messages,
      thread.isRunning
    )
      ? "queued"
      : "working";

    const id = appendUserBubble({
      text,
      images,
      sender: selectedUser,
      status,
      createdAt,
      parentMessageId,
    });
    if (!id) {
      sendLog.error({ preview: previewText(text) }, "submit failed to append bubble");
      return;
    }
    void composerRuntime.reset();
    queuedUserFingerprints.add(contentFingerprint("user", text));
    sendLog.info(
      { localId: id, queueStatus: status, images: images.length },
      "submit bubble appended"
    );

    if (status === "queued") {
      const item: QueuedSend = {
        id,
        text,
        images,
        sender: selectedUser,
        status: "queued",
        createdAt,
        parentMessageId,
      };
      const next = [...queueRef.current, item];
      queueRef.current = next;
      setQueue(next);
    }

    const requestId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `req-${Date.now()}`;
    pendingByRequestRef.current.set(requestId, {
      localId: id,
      text,
      images,
      sender: selectedUser,
      createdAt,
    });

    void persistUser({
      requestId,
      localId: id,
      text,
      images,
      sender: selectedUser,
      createdAt,
      queueStatus: status,
      parentMessageId,
    }).catch((err) => {
      sendLog.error({ err, requestId, localId: id }, "persistUser rejected");
      console.error("Failed to persist chat message", err);
    });
  }, [appendUserBubble, composerRuntime, persistUser, threadRuntime]);
}


function ChatUI({
  apiUrl,
  title,
  emptyState,
  className,
  pendingImages,
  onAddImages,
  onRemoveImage,
  users,
  onSwitchUser,
  conversationId,
  registerSender,
  registerImages,
  getSelectedUser,
  takePending,
  pushSendBatch,
  onProvider,
  onQueuedNextRef,
  chatSocket,
  aiServerSocket,
  maintainerProUrl,
  maintainerProApiKey,
  buildContext,
}: {
  apiUrl: string;
  title: string;
  emptyState?: ReactNode;
  className?: string;
  pendingImages: PendingImage[];
  onAddImages: (files: File[]) => void;
  onRemoveImage: (id: string) => void;
  users?: ChatUser[];
  onSwitchUser: () => void;
  conversationId?: string;
  registerSender: (messageId: string, sender: MessageSenderInfo) => void;
  registerImages: (
    messageId: string,
    items: Array<string | AttachmentView>
  ) => void;
  getSelectedUser: () => ChatUser | null;
  takePending: () => PendingImage[];
  pushSendBatch: (batch: SendBatch) => void;
  onProvider?: (providerId: string) => void;
  onQueuedNextRef?: {
    current: ((next: { id: string; content: string }) => void) | null;
  };
  chatSocket?: ChatSocket | null;
  aiServerSocket?: ChatSocket | null;
  maintainerProUrl?: string;
  maintainerProApiKey?: string;
  buildContext?: () => ClientContext;
}) {
  const [providerLabel, setProviderLabel] = useState("AI CLI");
  const [cliMissing, setCliMissing] = useState(false);
  const [cliChecking, setCliChecking] = useState(false);
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);
  const replyTargetRef = useRef<ReplyTarget | null>(null);
  replyTargetRef.current = replyTarget;
  const clearReplyTarget = useCallback(() => setReplyTarget(null), []);
  const replyApi = useMemo(
    () => ({ target: replyTarget, setTarget: setReplyTarget }),
    [replyTarget]
  );
  const [queue, setQueueState] = useState<QueuedSend[]>([]);
  const setQueue = useCallback((next: QueuedSend[]) => {
    setQueueState(next);
    syncQueuedFingerprints(next);
  }, []);
  const threadRuntimeForQueue = useThreadRuntime();
  useEffect(() => {
    const msgs = threadRuntimeForQueue.getState().messages;
    const restored: QueuedSend[] = [];
    for (const m of msgs) {
      if (m.role !== "user") continue;
      const meta = m.metadata?.custom as MessageCustom | undefined;
      if (meta?.queueStatus !== "queued") continue;
      restored.push({
        id: m.id,
        dbId: meta.dbId,
        text: getMessageText(m),
        images: [],
        sender: null,
        status: "queued",
        createdAt: meta.createdAt ?? new Date().toISOString(),
      });
    }
    if (restored.length > 0) setQueue(restored);
  }, [setQueue, threadRuntimeForQueue]);

  // After refresh / remote sync, rematch queue entries onto thread message ids.
  const threadRuntime = useThreadRuntime();
  useEffect(() => {
    if (queue.length === 0) return;
    const remap = () => {
      const msgs = threadRuntime.getState().messages;
      if (msgs.length === 0) return;
      let changed = false;
      const used = new Set<string>();
      const remapped = queue.map((item) => {
        if (msgs.some((m) => m.id === item.id)) return item;
        const match = [...msgs]
          .reverse()
          .find(
            (m) =>
              m.role === "user" &&
              !used.has(m.id) &&
              getMessageText(m).trim() === item.text.trim()
          );
        if (!match) return item;
        used.add(match.id);
        changed = true;
        return { ...item, id: match.id };
      });
      if (changed) setQueue(remapped);
    };
    remap();
    return threadRuntime.subscribe(remap);
  }, [queue, setQueue, threadRuntime]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queueApi = useMemo(() => ({ queue }), [queue]);
  const submit = useQueuedSubmit({
    getSelectedUser,
    requireLogin: (users ?? []).some(isComposableUser),
    apiUrl,
    conversationId,
    registerSender,
    registerImages,
    takePending,
    hasPendingAttachments: () => pendingImages.length > 0,
    pushSendBatch,
    queue,
    setQueue,
    onQueuedNextRef,
    chatSocket,
    aiServerSocket,
    maintainerProUrl,
    maintainerProApiKey,
    replyTargetRef,
    clearReplyTarget,
    buildContext,
  });
  const selectedUser = getSelectedUser();
  const isDeveloper = selectedUser?.type === "developer";
  const loginRequired = (users ?? []).some(isComposableUser);
  const canCompose = !loginRequired || Boolean(selectedUser);

  const refreshProvider = useCallback(() => {
    setCliChecking(true);
    return fetch(apiUrl)
      .then(async (res) => {
        const data = (await res.json()) as {
          provider?: string | null;
          providerLabel?: string;
          code?: string;
          error?: string;
        };
        const missing =
          !res.ok &&
          (data.code === "missing_cli" ||
            /no coding agent cli|no ai provider|cli is not available/i.test(
              data.error ?? ""
            ));
        setProviderLabel(
          res.ok && data.providerLabel ? data.providerLabel : "No AI CLI found"
        );
        setCliMissing(missing);
        if (res.ok && data.provider) {
          onProvider?.(data.provider);
        }
      })
      .catch(() => {
        setProviderLabel("No AI CLI found");
        setCliMissing(false);
      })
      .finally(() => {
        setCliChecking(false);
      });
  }, [apiUrl, onProvider]);

  useEffect(() => {
    let cancelled = false;
    void refreshProvider().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [refreshProvider]);

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (const item of Array.from(items)) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      event.preventDefault();
      onAddImages(files);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) onAddImages(files);
    event.target.value = "";
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canCompose) return;
    void submit();
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    ) {
      return;
    }
    event.preventDefault();
    if (!canCompose) return;
    void submit();
  };

  const mentionUsers = users ?? [];

  return (
    <SelectedUserContext.Provider value={selectedUser}>
    <ReplyTargetContext.Provider value={replyApi}>
    <MessageQueueContext.Provider value={queueApi}>
    <div className={`flex h-full min-h-0 flex-col overflow-hidden bg-gray-50 ${className ?? ""}`}>
      <div className="flex items-start justify-between gap-3 border-b border-gray-200 bg-white px-5 py-4 pr-12">
        <div className="min-w-0">
          <h2 className="font-semibold text-gray-800">{title}</h2>
          <p className="text-xs text-gray-400">Powered by {providerLabel}</p>
        </div>
        {selectedUser && (
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-600">
              {selectedUser.name}
              <span className="ml-1.5 font-semibold uppercase tracking-wide opacity-80">
                client
              </span>
            </span>
            <button
              type="button"
              onClick={onSwitchUser}
              className="text-[11px] text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
            >
              Switch user
            </button>
          </div>
        )}
      </div>

      <ThreadPrimitive.Root className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <ThreadPrimitive.Viewport className="aui-thread-scroll min-h-0 flex-1 overflow-y-auto overscroll-y-contain py-4 space-y-1">
          <ThreadPrimitive.Empty>
            {emptyState ?? <DefaultEmptyState />}
          </ThreadPrimitive.Empty>

          <ThreadPrimitive.Messages components={{ Message: MessageRenderer }} />
        </ThreadPrimitive.Viewport>

        <ThreadPrimitive.ViewportFooter className="bg-gray-50">
          <div className="flex justify-end px-4 pb-1">
            <ThreadPrimitive.ScrollToBottom className="rounded-full bg-white border border-gray-200 px-3 py-1 text-xs text-gray-500 shadow hover:bg-gray-50 transition-colors">
              Scroll to bottom
            </ThreadPrimitive.ScrollToBottom>
          </div>
          {cliMissing ? (
            <MissingCliBanner
              checking={cliChecking}
              onRecheck={() => void refreshProvider()}
            />
          ) : null}

          {!canCompose ? (
            <div className="border-t border-gray-200 bg-white px-4 py-5 text-center text-sm text-gray-500">
              Sign in to send messages.
            </div>
          ) : (
          <ComposerPrimitive.Root
            className="border-t border-gray-200 bg-white p-4"
            onSubmit={handleSubmit}
          >
            {replyTarget ? (
              <div className="mb-2 flex items-start justify-between gap-2 rounded-md border-l-[3px] border-indigo-400 bg-indigo-50 px-2.5 py-1.5 text-left text-[11px] text-indigo-900">
                <div className="min-w-0">
                  <p className="font-semibold truncate">
                    Replying to {replyTarget.name}
                  </p>
                  <p className="opacity-80 line-clamp-2">{replyTarget.snippet}</p>
                </div>
                <button
                  type="button"
                  className="shrink-0 text-indigo-500 hover:text-indigo-800"
                  onClick={clearReplyTarget}
                  aria-label="Cancel reply"
                >
                  ×
                </button>
              </div>
            ) : null}
            {pendingImages.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {pendingImages.map((img) => {
                  const image = isImageMime(img.mimeType);
                  return (
                    <div
                      key={img.id}
                      className={`group relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50 ${
                        image ? "h-16 w-16" : "max-w-[12rem] px-2 py-1.5"
                      }`}
                    >
                      {image ? (
                        <img
                          src={img.dataUrl}
                          alt={img.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <p className="pr-5 text-[11px] leading-4 text-gray-700">
                          <span className="mr-1 font-semibold uppercase text-gray-500">
                            {fileKindLabel(img.mimeType, img.name)}
                          </span>
                          <span className="break-all">{img.name}</span>
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => onRemoveImage(img.id)}
                        className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                        aria-label="Remove attachment"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-end gap-2 rounded-xl border border-gray-300 bg-white p-2 shadow-sm">
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACH_ACCEPT}
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-10 w-10 shrink-0 items-center justify-center self-end rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                aria-label="Attach file"
                title="Attach image, PDF, Word, or Excel"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-5 w-5"
                  aria-hidden
                >
                  <path
                    d="M21.44 11.05 12.25 20.24a6 6 0 1 1-8.49-8.49l10.6-10.6a4 4 0 0 1 5.66 5.66l-10.6 10.61a2 2 0 1 1-2.83-2.83l9.19-9.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <ComposerMentionInput
                users={mentionUsers}
                excludeUser={selectedUser}
                onPaste={handlePaste}
                onSubmitKeyDown={handleInputKeyDown}
              />
              <ComposerSendButton hasAttachments={pendingImages.length > 0} />
            </div>
            <p className="mt-2 text-[11px] text-gray-400">
              {`Paste a screenshot or attach PDF, Word, or Excel (up to ${MAX_ATTACHMENTS} files). Type @ to tag someone.`}
            </p>
          </ComposerPrimitive.Root>
          )}
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Root>
    </div>
    </MessageQueueContext.Provider>
    </ReplyTargetContext.Provider>
    </SelectedUserContext.Provider>
  );
}

export function AiChat({
  apiUrl = "/api/chat",
  buildContext,
  onToolCalls,
  captureState,
  restoreState,
  conversationId: conversationIdProp,
  title = "AI Assistant",
  emptyState,
  className,
  users,
  maintainerProUrl,
  maintainerProApiKey,
  aiServerWsUrl,
}: AiChatProps) {
  const resolvedBuildContext =
    buildContext ?? (() => buildBrowserClientContext());
  const [boot, setBoot] = useState<
    | {
        conversationId: string;
        initialMessages: ThreadMessageLike[];
        initialSenders: Record<string, MessageSenderInfo>;
        initialImages: Record<string, AttachmentView[]>;
        providerId: string | null;
        users: ChatUser[];
        sessionUser: ChatUser | null;
      }
    | { error: string }
    | null
  >(null);

  useEffect(() => {
    let cancelled = false;

    async function bootChat() {
      try {
        log.debug(
          {
            apiUrl,
            conversationIdProp,
            mp: Boolean(maintainerProUrl && maintainerProApiKey),
          },
          "boot start"
        );
        const providerRes = await fetch(apiUrl).catch(() => null);
        const providerData = providerRes
          ? ((await providerRes.json().catch(() => ({}))) as {
              conversationId?: string | null;
              provider?: string | null;
              messages?: Array<{
                id?: string;
                role: string;
                content: string;
                provider?: string | null;
                senderType?: string | null;
                senderName?: string | null;
                parentMessageId?: string | null;
                queueStatus?: string | null;
                queuePosition?: number | null;
                attachmentIds?: string[];
              }>;
            })
          : {};

        let conversationId =
          conversationIdProp ?? providerData.conversationId ?? "shared";
        let storedMessages = providerData.messages ?? [];
        let resolvedUsers = users ?? [];
        let sessionUser: ChatUser | null = null;
        const mpConfigured = Boolean(maintainerProUrl && maintainerProApiKey);
        const mp = mpConfigured
          ? createMaintainerProClient({
              baseUrl: maintainerProUrl!,
              apiKey: maintainerProApiKey!,
            })
          : null;

        if (mp) {
          if (!conversationIdProp) {
            const conversations = await mp.listConversations();
            conversationId = conversations[0]?.id ?? "shared";
          }
          if (conversationId && conversationId !== "shared") {
            rememberConversationId(
              maintainerProUrl!,
              maintainerProApiKey!,
              conversationId
            );
          }
          storedMessages = await mp.listMessages(conversationId);
          if (users === undefined) {
            resolvedUsers = await mp.listChatUsers();
          }
          const sameOrigin = await readSameOriginClientUser();
          let session: ChatUser | null = sameOrigin;
          if (!session) {
            try {
              const fromApi = await mp.chatSession();
              if (
                fromApi?.email &&
                (fromApi.type === "client" || fromApi.type === "developer")
              ) {
                session = {
                  name: fromApi.name,
                  email: fromApi.email,
                  type: fromApi.type,
                  role: fromApi.role,
                };
              }
            } catch (err) {
              log.debug({ err }, "chat-session unavailable");
            }
          }
          if (session?.type === "developer" && session.email) {
            sessionUser = session;
            log.info({ email: session.email }, "partner session applied");
          } else if (session?.email) {
            const allowed = resolvedUsers.some(
              (u) =>
                u.type === "client" &&
                u.email &&
                u.email.toLowerCase() === session!.email!.toLowerCase()
            );
            if (allowed) {
              sessionUser = session;
              log.info({ email: session.email }, "client session applied");
            } else {
              log.debug(
                { email: session.email },
                "client session skipped (not a user of this app)"
              );
            }
          }
        }

        const initialImages: Record<string, AttachmentView[]> = {};
        if (mp) {
          await Promise.all(
            storedMessages.map(async (m, index) => {
              const ids = "attachmentIds" in m ? m.attachmentIds : undefined;
              if (!ids?.length) return;
              const id = m.id ?? `boot-${index}`;
              const meta =
                "attachments" in m && Array.isArray(m.attachments)
                  ? m.attachments
                  : [];
              try {
                initialImages[id] = await Promise.all(
                  ids.map(async (attId) => {
                    const info = meta.find((row) => row.id === attId);
                    return {
                      url: await mp.fetchAttachmentObjectUrl(attId),
                      mimeType: info?.mimeType,
                      name: info?.originalName,
                    };
                  })
                );
              } catch (err) {
                log.warn({ err, messageId: id }, "attachment hydrate failed");
              }
            })
          );
        }

        const providerId = providerData.provider ?? null;
        const aiFallback = resolveAiUser(resolvedUsers, providerId);
        const initialSenders: Record<string, MessageSenderInfo> = {};
        const initialMessages: ThreadMessageLike[] = storedMessages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m, index) => {
            const isWorking = m.provider === "working";
            const sender = senderFromStoredMessage(m, aiFallback);
            const id = m.id ?? `boot-${index}`;
            if (sender) {
              initialSenders[id] = sender;
            }
            const parentMessageId =
              "parentMessageId" in m && typeof m.parentMessageId === "string"
                ? m.parentMessageId
                : undefined;
            const createdAt =
              "createdAt" in m && typeof m.createdAt === "string"
                ? m.createdAt
                : undefined;
            return {
              id,
              role: m.role as "user" | "assistant",
              content: [
                {
                  type: "text" as const,
                  text: isWorking ? "" : m.content || "",
                },
              ],
              metadata: {
                custom: {
                  ...(sender
                    ? {
                        senderType: sender.type,
                        senderName: sender.name,
                      }
                    : {}),
                  ...(isWorking ? { working: true } : {}),
                  ...(parentMessageId ? { parentMessageId } : {}),
                  ...(createdAt ? { createdAt } : {}),
                  ...("queueStatus" in m && m.queueStatus
                    ? { queueStatus: m.queueStatus }
                    : {}),
                  ...(m.id ? { dbId: m.id } : {}),
                },
              },
            };
          });

        if (!cancelled) {
          log.info(
            {
              conversationId,
              messages: initialMessages.length,
              users: resolvedUsers.length,
              providerId,
            },
            "boot ok"
          );
          setBoot({
            conversationId,
            initialMessages,
            initialSenders,
            initialImages,
            providerId,
            users: resolvedUsers,
            sessionUser,
          });
        }
      } catch (err) {
        if (cancelled) return;
        log.error({ err }, "boot failed");
        const message =
          err instanceof Error && err.message.trim()
            ? err.message.trim()
            : "Failed to load chat";
        if (maintainerProUrl && maintainerProApiKey) {
          setBoot({ error: message });
          return;
        }
        setBoot({
          conversationId: conversationIdProp ?? "shared",
          initialMessages: [],
          initialSenders: {},
          initialImages: {},
          providerId: null,
          users: users ?? [],
          sessionUser: null,
        });
      }
    }

    void bootChat();

    return () => {
      cancelled = true;
    };
  }, [
    apiUrl,
    conversationIdProp,
    maintainerProUrl,
    maintainerProApiKey,
    users,
  ]);

  if (!boot) {
    return (
      <div
        className={`flex h-full items-center justify-center bg-gray-50 text-sm text-gray-400 ${className ?? ""}`}
      >
        Loading chat…
      </div>
    );
  }

  if ("error" in boot) {
    return (
      <div
        className={`flex h-full flex-col items-center justify-center gap-2 bg-gray-50 px-6 text-center ${className ?? ""}`}
      >
        <p className="text-sm font-medium text-gray-800">Chat unavailable</p>
        <p className="max-w-sm text-xs leading-relaxed text-gray-500">
          {boot.error}
        </p>
      </div>
    );
  }

  return (
    <AiChatReady
      apiUrl={apiUrl}
      buildContext={resolvedBuildContext}
      onToolCalls={onToolCalls}
      captureState={captureState}
      restoreState={restoreState}
      conversationId={boot.conversationId}
      initialMessages={boot.initialMessages}
      initialSenders={boot.initialSenders}
      initialImages={boot.initialImages}
      providerId={boot.providerId}
      title={title}
      emptyState={emptyState}
      className={className}
      users={boot.users}
      sessionUser={boot.sessionUser}
      maintainerProUrl={maintainerProUrl}
      maintainerProApiKey={maintainerProApiKey}
      aiServerWsUrl={aiServerWsUrl}
    />
  );
}

function AiChatReady({
  apiUrl,
  buildContext,
  onToolCalls,
  captureState,
  restoreState,
  conversationId,
  initialMessages,
  initialSenders,
  initialImages,
  providerId,
  title,
  emptyState,
  className,
  users,
  sessionUser,
  maintainerProUrl,
  maintainerProApiKey,
  aiServerWsUrl,
}: {
  apiUrl: string;
  buildContext: () => ClientContext;
  onToolCalls?: (toolCalls: ToolCall[]) => void | Promise<void>;
  captureState?: () => unknown;
  restoreState?: (snapshot: unknown) => void;
  conversationId: string;
  initialMessages: readonly ThreadMessageLike[];
  initialSenders: Record<string, MessageSenderInfo>;
  initialImages: Record<string, AttachmentView[]>;
  providerId: string | null;
  title: string;
  emptyState?: ReactNode;
  className?: string;
  users?: ChatUser[];
  sessionUser?: ChatUser | null;
  maintainerProUrl?: string;
  maintainerProApiKey?: string;
  aiServerWsUrl?: string;
}) {
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [imagesByMessageId, setImagesByMessageId] =
    useState<Record<string, AttachmentView[]>>(initialImages);
  const [senderByMessageId, setSenderByMessageId] =
    useState<Record<string, MessageSenderInfo>>(initialSenders);
  const [activeProviderId, setActiveProviderId] = useState<string | null>(
    providerId
  );
  const [extraUsers, setExtraUsers] = useState<ChatUser[]>(() => {
    if (!isAuthedSessionUser(sessionUser)) {
      return [];
    }
    const exists = (users ?? []).some(
      (u) =>
        u.email &&
        u.email.toLowerCase() === sessionUser.email!.toLowerCase()
    );
    return exists ? [] : [sessionUser];
  });
  const allUsers = useMemo(
    () => [...(users ?? []), ...extraUsers],
    [users, extraUsers]
  );
  const aiUser = useMemo(
    () => resolveAiUser(allUsers, activeProviderId),
    [allUsers, activeProviderId]
  );
  const onProvider = useCallback((id: string) => {
    setActiveProviderId((prev) => (prev === id ? prev : id));
  }, []);
  const composableUsers = useMemo(
    () => allUsers.filter(isComposableUser),
    [allUsers]
  );
  const [selectedUserKey, setSelectedUserKey] = useState<string | null>(() => {
    const initial = [
      ...(users ?? []),
      ...(isAuthedSessionUser(sessionUser) &&
      !(users ?? []).some(
        (u) =>
          u.email &&
          u.email.toLowerCase() === sessionUser.email!.toLowerCase()
      )
        ? [sessionUser]
        : []),
    ].filter(isComposableUser);
    if (isAuthedSessionUser(sessionUser)) {
      const fromSession = keyForClientEmail(initial, sessionUser.email);
      if (fromSession) return fromSession;
    }
    return readStoredUserKey(conversationId, initial);
  });
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const pendingRef = useRef<PendingImage[]>([]);
  pendingRef.current = pendingImages;
  const turnRef = useRef<TurnControl>({
    snapshot: undefined,
    toolsApplied: false,
  });
  const selectedUserRef = useRef<ChatUser | null>(null);

  const getSelectedUser = useCallback((): ChatUser | null => {
    if (composableUsers.length === 0 || !selectedUserKey) return null;
    const index = composableUsers.findIndex(
      (u, i) => userKey(u, i) === selectedUserKey
    );
    return index >= 0 ? composableUsers[index]! : null;
  }, [composableUsers, selectedUserKey]);

  selectedUserRef.current = getSelectedUser();

  useEffect(() => {
    if (!maintainerProUrl || !maintainerProApiKey) {
      setGoogleEnabled(false);
      return;
    }
    const mp = createMaintainerProClient({
      baseUrl: maintainerProUrl,
      apiKey: maintainerProApiKey,
    });
    void mp
      .authProviders()
      .then((data) => setGoogleEnabled(Boolean(data.google)))
      .catch(() => setGoogleEnabled(false));
  }, [maintainerProApiKey, maintainerProUrl]);

  const applyAuthedUser = useCallback(
    (authed: {
      name: string;
      email: string;
      type?: "client" | "developer";
      role?: string;
    }) => {
      const added: ChatUser = {
        name: authed.name,
        email: authed.email,
        type: authed.type === "developer" ? "developer" : "client",
        role: authed.role,
      };
      const already = composableUsers.findIndex(
        (u) =>
          u.email &&
          u.email.toLowerCase() === authed.email.toLowerCase()
      );
      const nextUsers =
        already >= 0 ? composableUsers : [...composableUsers, added];
      if (already < 0) {
        setExtraUsers((prev) =>
          prev.some(
            (u) => u.email?.toLowerCase() === authed.email.toLowerCase()
          )
            ? prev
            : [...prev, added]
        );
      }
      const index = already >= 0 ? already : nextUsers.length - 1;
      const key = userKey(nextUsers[index]!, index);
      setSelectedUserKey(key);
      writeStoredUserKey(conversationId, key);
    },
    [composableUsers, conversationId]
  );

  const handlePasswordSignIn = useCallback(
    async (email: string, password: string) => {
      if (!maintainerProUrl || !maintainerProApiKey) {
        throw new Error("Maintainer Pro is not configured");
      }
      const mp = createMaintainerProClient({
        baseUrl: maintainerProUrl,
        apiKey: maintainerProApiKey,
      });
      const authed = await mp.loginChatUser(email, password);
      applyAuthedUser(authed);
    },
    [applyAuthedUser, maintainerProApiKey, maintainerProUrl]
  );

  const handleGoogleSignIn = useCallback(async (popup: Window | null) => {
    if (!maintainerProUrl || !maintainerProApiKey) {
      throw new Error("Maintainer Pro is not configured");
    }
    if (!popup) {
      throw new Error("Allow popups to sign in with Google");
    }
    const mp = createMaintainerProClient({
      baseUrl: maintainerProUrl,
      apiKey: maintainerProApiKey,
    });
    try {
      const url = await mp.startChatGoogle(window.location.origin);
      popup.location.href = url;
      popup.focus();
    } catch (err) {
      popup.close();
      throw err;
    }
    const adminOrigin = new URL(maintainerProUrl).origin;
    const authed = await waitForChatGoogleMessage(adminOrigin, popup);
    applyAuthedUser(authed);
  }, [
    applyAuthedUser,
    maintainerProApiKey,
    maintainerProUrl,
  ]);

  const handleSwitchUser = useCallback(() => {
    setSelectedUserKey(null);
    writeStoredUserKey(conversationId, null);
  }, [conversationId]);

  const registerImages = useCallback(
    (messageId: string, items: Array<string | AttachmentView>) => {
      setImagesByMessageId((prev) => ({
        ...prev,
        [messageId]: items.map(toAttachmentView),
      }));
    },
    []
  );

  const registerSender = useCallback(
    (messageId: string, sender: MessageSenderInfo) => {
      setSenderByMessageId((prev) => {
        const existing = prev[messageId];
        if (
          existing &&
          existing.name === sender.name &&
          existing.type === sender.type
        ) {
          return prev;
        }
        return { ...prev, [messageId]: sender };
      });
    },
    []
  );

  const takePending = useCallback((): PendingImage[] => {
    const current = pendingRef.current;
    pendingRef.current = [];
    setPendingImages([]);
    return current;
  }, []);

  const queuedNextRef = useRef<
    ((next: { id: string; content: string }) => void) | null
  >(null);
  const [chatSocket, setChatSocket] = useState<ChatSocket | null>(null);
  const [aiServerSocket, setAiServerSocket] = useState<ChatSocket | null>(null);
  useEffect(() => {
    if (!maintainerProUrl || !maintainerProApiKey || !conversationId) {
      setChatSocket(null);
      return;
    }
    sendLog.info({ conversationId, maintainerProUrl }, "connect mp websocket");
    const socket = connectChatSocket({
      maintainerProUrl,
      apiKey: maintainerProApiKey,
      conversationExternalId: conversationId,
    });
    setChatSocket(socket);
    return () => {
      socket.stop();
      setChatSocket(null);
    };
  }, [conversationId, maintainerProApiKey, maintainerProUrl]);
  useEffect(() => {
    if (!conversationId || !apiUrl) {
      setAiServerSocket(null);
      return;
    }
    const origin =
      typeof window !== "undefined" ? window.location.origin : undefined;
    const url = toAiServerWsUrl(aiServerWsUrl || apiUrl, origin);
    sendLog.info({ conversationId, url }, "connect ai-server websocket");
    const socket = connectAiServerSocket({
      url,
      conversationId,
    });
    setAiServerSocket(socket);
    return () => {
      socket.stop();
      setAiServerSocket(null);
    };
  }, [aiServerWsUrl, apiUrl, conversationId]);
  const sendBatchRef = useRef<SendBatch[]>([]);
  const pushSendBatch = useCallback((batch: SendBatch) => {
    sendBatchRef.current.push(batch);
  }, []);
  const takeSendBatch = useCallback((): SendBatch | null => {
    return sendBatchRef.current.shift() ?? null;
  }, []);

  const onAddImages = useCallback(async (files: File[]) => {
    try {
      const next: PendingImage[] = [];
      for (const file of files) {
        next.push(await fileToPending(file));
      }
      setPendingImages((prev) => [...prev, ...next].slice(0, MAX_ATTACHMENTS));
    } catch (err) {
      console.error(err);
      window.alert(err instanceof Error ? err.message : "Could not add file");
    }
  }, []);

  const onRemoveImage = useCallback((id: string) => {
    setPendingImages((prev) => prev.filter((img) => img.id !== id));
  }, []);

  const adapter = useStableAdapter({
    apiUrl,
    buildContext,
    onToolCalls,
    captureState,
    restoreState,
    conversationId,
    takePending,
    takeSendBatch,
    registerImages,
    registerSender,
    getSelectedUser: () => selectedUserRef.current,
    onProvider,
    turnRef,
    onQueuedNextRef: queuedNextRef,
    aiServerSocket,
  });
  const runtime = useLocalRuntime(adapter, { initialMessages });

  const imagesApi = useMemo(
    () => ({ imagesByMessageId, registerImages }),
    [imagesByMessageId, registerImages]
  );

  const sendersApi = useMemo(
    () => ({ senderByMessageId, registerSender }),
    [senderByMessageId, registerSender]
  );

  // Users from Maintainer Pro (or an explicit users list) must sign in first.
  const needsLogin =
    (Boolean(maintainerProUrl && maintainerProApiKey) ||
      composableUsers.length > 0) &&
    !selectedUserKey;

  if (needsLogin) {
    return (
      <ChatLoginScreen
        title={title}
        className={className}
        googleEnabled={googleEnabled}
        onPasswordSignIn={handlePasswordSignIn}
        onGoogleSignIn={handleGoogleSignIn}
      />
    );
  }

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ChatUsersContext.Provider value={allUsers}>
        <AiUserContext.Provider value={aiUser}>
          <MessageImagesContext.Provider value={imagesApi}>
            <MessageSendersContext.Provider value={sendersApi}>
              <RemoteMessageSync
                apiUrl={apiUrl}
                conversationId={conversationId}
                maintainerProUrl={maintainerProUrl}
                maintainerProApiKey={maintainerProApiKey}
                aiUser={aiUser}
                registerSender={registerSender}
                chatSocket={chatSocket}
                aiServerSocket={aiServerSocket}
              />
              <ChatUI
                apiUrl={apiUrl}
                title={title}
                emptyState={emptyState}
                className={className}
                pendingImages={pendingImages}
                onAddImages={onAddImages}
                onRemoveImage={onRemoveImage}
                users={users}
                onSwitchUser={handleSwitchUser}
                conversationId={conversationId}
                registerSender={registerSender}
                registerImages={registerImages}
                getSelectedUser={getSelectedUser}
                takePending={takePending}
                pushSendBatch={pushSendBatch}
                onProvider={onProvider}
                onQueuedNextRef={queuedNextRef}
                chatSocket={chatSocket}
                aiServerSocket={aiServerSocket}
                maintainerProUrl={maintainerProUrl}
                maintainerProApiKey={maintainerProApiKey}
                buildContext={buildContext}
              />
            </MessageSendersContext.Provider>
          </MessageImagesContext.Provider>
        </AiUserContext.Provider>
      </ChatUsersContext.Provider>
    </AssistantRuntimeProvider>
  );
}

function threadMessagePlainText(message: ThreadMessage): string {
  const content = message.content as unknown;
  if (typeof content === "string") return content.trim();
  if (!Array.isArray(content)) return "";
  return content
    .map((part) => {
      if (
        part &&
        typeof part === "object" &&
        (part as { type?: string }).type === "text" &&
        typeof (part as { text?: unknown }).text === "string"
      ) {
        return (part as { text: string }).text;
      }
      return "";
    })
    .join("")
    .trim();
}

/** Role + body only — sender metadata is often missing on local thread msgs. */
function contentFingerprint(role: string, content: string): string {
  return `${role}|${content.trim()}`;
}

/**
 * Sync store / Maintainer Pro messages that arrived from other sessions
 * while this chat is open. Uses WebSocket when Maintainer Pro is configured;
 * otherwise falls back to a local HTTP poll against apiUrl.
 */
function RemoteMessageSync({
  apiUrl,
  conversationId,
  maintainerProUrl,
  maintainerProApiKey,
  aiUser,
  registerSender,
  chatSocket,
  aiServerSocket,
  pollIntervalMs = 4000,
}: {
  apiUrl: string;
  conversationId: string;
  maintainerProUrl?: string;
  maintainerProApiKey?: string;
  aiUser: MessageSenderInfo;
  registerSender: (messageId: string, sender: MessageSenderInfo) => void;
  chatSocket?: ChatSocket | null;
  aiServerSocket?: ChatSocket | null;
  pollIntervalMs?: number;
}) {
  const threadRuntime = useThreadRuntime();
  const seenRemoteIdsRef = useRef<Set<string>>(new Set());
  const baselineReadyRef = useRef(false);
  const threadRuntimeRef = useRef(threadRuntime);
  threadRuntimeRef.current = threadRuntime;
  const aiUserRef = useRef(aiUser);
  aiUserRef.current = aiUser;
  const registerSenderRef = useRef(registerSender);
  registerSenderRef.current = registerSender;

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    baselineReadyRef.current = false;
    seenRemoteIdsRef.current = new Set();

    if (maintainerProUrl && maintainerProApiKey && conversationId) {
      rememberConversationId(
        maintainerProUrl,
        maintainerProApiKey,
        conversationId
      );
    }

    const appendRemote = (m: StoredChatMessage) => {
      if (m.role !== "user" && m.role !== "assistant") return;
      const isWorking = m.provider === "working";
      const content = (m.content ?? "").trim();
      if (!content && !isWorking) return;

      const runtime = threadRuntimeRef.current;
      const parentRef =
        typeof m.parentMessageId === "string" && m.parentMessageId
          ? m.parentMessageId
          : null;
      const sender = senderFromStoredMessage(m, aiUserRef.current);
      const custom = {
        ...(sender
          ? {
              senderType: sender.type,
              senderName: sender.name,
            }
          : {}),
        ...(isWorking ? { working: true } : {}),
        ...(parentRef ? { parentMessageId: parentRef } : {}),
        ...(m.createdAt ? { createdAt: m.createdAt } : {}),
        ...(m.queueStatus !== undefined
          ? { queueStatus: m.queueStatus }
          : {}),
        ...(m.id ? { dbId: m.id } : {}),
      };
      const threadMessage = {
        id: m.id ?? `remote-${Date.now()}`,
        role: m.role as "user" | "assistant",
        content: [{ type: "text" as const, text: isWorking ? "" : content }],
        createdAt: m.createdAt ? new Date(m.createdAt) : new Date(),
        attachments: [],
        metadata: { custom },
        status: { type: "complete" as const, reason: "unknown" as const },
      } as ThreadMessage;

      if (m.id) {
        const existing = findLocalByRemoteId(runtime, m.id);
        if (existing || seenRemoteIdsRef.current.has(m.id)) {
          seenRemoteIdsRef.current.add(m.id);
          // Admin upsert: merge in place (content + queue metadata).
          upsertRemoteLinear(runtime, {
            ...threadMessage,
            id: existing?.id ?? m.id,
          });
          if (sender) registerSenderRef.current(existing?.id ?? m.id, sender);
          return;
        }
      }

      const seenKey =
        m.id ??
        (isWorking
          ? contentFingerprint("assistant", `__working__:${content || "x"}`)
          : contentFingerprint(m.role, content));
      if (seenRemoteIdsRef.current.has(seenKey)) return;

      const local = runtime.getState().messages;
      const localContents = new Set(
        local.map((msg) =>
          contentFingerprint(msg.role, threadMessagePlainText(msg))
        )
      );
      const localRunning = runtime.getState().isRunning;

      if (isWorking && localRunning) {
        seenRemoteIdsRef.current.add(seenKey);
        return;
      }

      // No source id — last-resort content match for older events.
      if (!m.id) {
        const key = isWorking
          ? contentFingerprint("assistant", `__working__:${content || "x"}`)
          : contentFingerprint(m.role, content);
        if (localContents.has(key)) {
          seenRemoteIdsRef.current.add(seenKey);
          return;
        }
        if (m.role === "user" && queuedUserFingerprints.has(key)) {
          seenRemoteIdsRef.current.add(seenKey);
          return;
        }
      }

      // Claim before append so Strict Mode / overlapping sync can't double-add.
      seenRemoteIdsRef.current.add(seenKey);

      // Same as admin upsertLiveMessage: append chronologically.
      upsertRemoteLinear(runtime, threadMessage);
      if (m.id && sender) registerSenderRef.current(m.id, sender);
      log.debug(
        {
          id: threadMessage.id,
          role: m.role,
          parentRef,
          working: isWorking,
          preview: previewText(content),
        },
        "appendRemote upsert/append"
      );
    };

    const sync = async (mode: "baseline" | "catchup" = "catchup") => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const remote = await fetchChatMessages({
          apiUrl,
          conversationId,
          maintainerProUrl,
          maintainerProApiKey,
        });
        if (cancelled) return;

        if (!baselineReadyRef.current || mode === "baseline") {
          for (const msg of threadRuntimeRef.current.getState().messages) {
            for (const id of localMessageIds(msg)) {
              seenRemoteIdsRef.current.add(id);
            }
          }
          for (const m of remote) {
            if (m.id) {
              seenRemoteIdsRef.current.add(m.id);
              continue;
            }
            const isWorking = m.provider === "working";
            const content = (m.content ?? "").trim();
            if (isWorking) {
              seenRemoteIdsRef.current.add(
                contentFingerprint("assistant", `__working__:${content || "x"}`)
              );
            } else if (content) {
              seenRemoteIdsRef.current.add(
                contentFingerprint(m.role, content)
              );
            }
          }
          baselineReadyRef.current = true;
          // Boot already hydrated the thread — only append on later catch-up / events.
          if (mode === "baseline") return;
        }

        for (const m of remote) {
          appendRemote(m);
        }
      } catch {
        // ignore transient errors
      } finally {
        inFlight = false;
      }
    };

    const useWs = Boolean(maintainerProUrl && maintainerProApiKey);
    if (!useWs && !chatSocket && !aiServerSocket) {
      void sync("baseline");
    }

    let timer: ReturnType<typeof setInterval> | null = null;
    const unsubs: Array<() => void> = [];

    const onWsMessage = (msg: Record<string, unknown>) => {
      if (msg.type === "message.created") {
        const message = msg.message as StoredChatMessage | undefined;
        if (message) appendRemote(message);
        return;
      }
      if (msg.type === "message.deleted") {
        const ids = Array.isArray(msg.ids)
          ? msg.ids.filter((id): id is string => typeof id === "string")
          : [];
        dropWorkingPlaceholders(threadRuntimeRef.current, ids);
        return;
      }
      if (msg.type === "message.updated") {
        const message = msg.message as StoredChatMessage | undefined;
        if (!message?.id) return;
        const existing = findLocalByRemoteId(
          threadRuntimeRef.current,
          message.id
        );
        if (existing) {
          seenRemoteIdsRef.current.add(message.id);
          patchMessageCustom(
            threadRuntimeRef.current,
            { localId: existing.id, dbId: message.id },
            {
              dbId: message.id,
              ...(message.queueStatus !== undefined
                ? { queueStatus: message.queueStatus }
                : {}),
              ...(message.createdAt ? { createdAt: message.createdAt } : {}),
            }
          );
          return;
        }
        appendRemote(message);
      }
    };

    const hasLiveSocket = Boolean(chatSocket || aiServerSocket);
    if (hasLiveSocket || useWs) {
      for (const msg of threadRuntimeRef.current.getState().messages) {
        for (const id of localMessageIds(msg)) {
          seenRemoteIdsRef.current.add(id);
        }
      }
      baselineReadyRef.current = true;
    }

    if (chatSocket) {
      unsubs.push(chatSocket.subscribe(onWsMessage));
    }
    if (aiServerSocket) {
      unsubs.push(aiServerSocket.subscribe(onWsMessage));
    }
    if (!chatSocket && !aiServerSocket && useWs && maintainerProUrl && maintainerProApiKey) {
      const fallback = startChatRealtime({
        maintainerProUrl,
        apiKey: maintainerProApiKey,
        conversationExternalId: conversationId || undefined,
        onMessage: onWsMessage,
      });
      unsubs.push(fallback.stop);
    } else if (!chatSocket && !aiServerSocket) {
      timer = setInterval(() => {
        void sync("catchup");
      }, pollIntervalMs);
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      for (const stop of unsubs) stop();
    };
  }, [
    apiUrl,
    conversationId,
    maintainerProUrl,
    maintainerProApiKey,
    pollIntervalMs,
    chatSocket,
    aiServerSocket,
  ]);

  return null;
}
